/**
 * usePayrollBusinessLogic.ts - Core business logic for payroll management
 *
 * Phase 2: Extracted from payroll-management.tsx
 * Contains: State declarations, field tracking, employee CRUD, team operations,
 * data persistence, incentive calculations, and all business useEffects.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Employee, TeamData, BudgetData, TeamIncentiveCalculation } from '../../contexts/AppContext.tsx';
import { TeamDataUtils } from '../../utils/teamDataUtils.ts';
import {
  calculateSalaryTotal,
  calculatePureTargetSales,
  calculateTeamStatistics,
  calculateTeamSalaryTotal,
  resolveTeamIncentiveRatio,
  countOvertimeEligibleEmployees,
  recalcOvertimePay,
  calculateOvertimePay,
  clearPartPayFields,
  isPartPosition,
  resolveRetirementPay,
  normalizeEmployeeForSave,
  resolveFinalTargetSales,
  PART_LOCKED_FIELDS,
  OVERTIME_WORKPLACE_MIN_EMPLOYEES
} from '../../utils/payrollCalculations.ts';
import { getSafeMonthlyData } from '../../utils/payrollDataUtils.ts';
import { deriveMonthTargets } from '../../utils/payrollMonthTargets.ts';
import { buildLimitedPayrollPatch } from '../../utils/payrollPartialSave.ts';
import { getPayrollEditMode } from '../../utils/payrollPermissions.ts';
import {
  logDataOperation,
  logAdjustment,
  logTeamOperation,
  logPerformanceCritical,
  logError,
  logBusinessEvent,
  logUserAction,
  logDebugDetails,
  startLogGroup,
  endLogGroup
} from '../../utils/payrollLogger.ts';
import {
  useConditionalEffect
} from '../usePerformanceOptimization.ts';
import {
  IS_DEVELOPMENT,
  DEBUG_TARGET_SALES_CALCULATION,
  DEBUG_PREFIX,
  debugLog,
  debugGroup,
  debugCalculationStep,
  devLog,
  generateUniqueName,
} from '../../utils/payrollDebugUtils.ts';
import { usePayrollUI, usePayrollLoadingContext, usePayrollSettingsContext } from '../../contexts/PayrollContext.tsx';
import { usePayrollCalculations } from './usePayrollCalculations.ts';
import { useMonthlyData } from './useMonthlyData.ts';

import { getBudgetData, saveBudgetMonthReplace, saveBudgetMonthFieldsAndGlobal, GLOBAL_TEAM_MAP_FIELDS } from '../../services/firebase/budgetFirebaseService.ts';
import { saveEmployees, isValidEmployeeDocId } from '../../services/firebase/employeeFirebaseService.ts';
import { deleteTeam } from '../../services/firebase/teamFirebaseService.ts';

// ═══════════════════════════════════════════════════════════════════
// 🔒 급여 저장 '소유 필드' 화이트리스트 — internalSaveAllData·saveSpecificMonthData 공용 단일 정본.
//    두 저장 경로가 각자 필드 목록을 들고 있으면 드리프트로 편집 유실이 재발하므로 여기서만 관리한다.
//    여기 없는 월 필드(actualSales 캐시·totalActualSales·calculatedAt·doctorTeamMapping·teamActualSales 등)는
//    실적/성과 파이프라인 소유 — 급여 저장이 절대 쓰지 않는다 (2026-06 의사매핑 소실 사고 재발 방지).
// ═══════════════════════════════════════════════════════════════════

// merge(부분병합) 저장 필드: undefined 값은 저장 시 스킵되어 DB 값이 보존된다.
// adjustmentApplied는 팀 키맵이지만 해제가 '명시적 false 기록'이라 merge로 충분
// (replace로 다루면 in-memory 미적재 시 DB 플래그가 통째 삭제될 위험).
const PAYROLL_OWNED_MONTH_MERGE_FIELDS = [
  'targetSales',
  'personnelPercentage',
  'underperformanceDeductionRate',
  'employees',
  'teams',
  'lastUpdated',
  'selectedMonth',
  'dataSource',
  'applyOvertimeMultiplier',
  'applyPreviousMonthAdjustment',
  'adjustmentApplied',
] as const;

// replace(dot-path 통째 교체) 저장 팀맵 필드: merge는 빠진 키를 못 지우므로
// 삭제된 팀의 유령 키 정리를 위해 통째 교체가 필요한 급여 소유 맵들.
// ⚠️ teamPerformanceData는 '급여가 재계산 시에만 기록': 재계산·오귀속 게이트를 거치는
//    internalSaveAllData만 replace하고, 재계산 없는 saveSpecificMonthData(월 전환 프롬프트 저장)는
//    비접촉(스냅숏 replace는 {} 소거·stale 역행 위험 — 각 저장 경로에서 개별 제외).
const PAYROLL_OWNED_MONTH_TEAM_MAP_FIELDS = [
  'teamTargetSales',
  // 🔒 최종목표와 반드시 같은 저장에 실린다 — 불변식(목표 = 순수 + 이월)이 DB에서 성립하려면
  //    두 값이 따로 저장되는 창이 없어야 한다.
  'teamAdjustmentCarryover',
  'teamPersonnelPercentages',
  'teamIncentiveRatios',
  'teamSalaryTotals',
  'teamIncentiveCalculations',
  'teamPerformanceData',
  'teamPureTargetSales',
] as const;

export interface PayrollBusinessLogicParams {
  // From props
  selectedMonth: string;
  // From auth
  user: any;
  canEditPayroll: boolean;
  // From AppContext
  budgetData: BudgetData;
  setBudgetData: (fn: any) => void;
  globalEmployees: Employee[];
  setGlobalEmployees: (employees: Employee[]) => void;
  teamsData: TeamData[];
  setTeamsContext: (fn: any) => void;
  saveTeamData: (teams: TeamData[]) => Promise<void>;
  addDeletedEmployeeName: (name: string) => void;
  deletedEmployeeNames: string[];
  clearDeletedEmployeeNames: () => void;
  isLoading: boolean;
}

export function usePayrollBusinessLogic(params: PayrollBusinessLogicParams) {
  const {
    selectedMonth,
    user,
    canEditPayroll,
    budgetData,
    setBudgetData,
    globalEmployees,
    setGlobalEmployees,
    teamsData,
    setTeamsContext,
    saveTeamData,
    addDeletedEmployeeName,
    deletedEmployeeNames,
    clearDeletedEmployeeNames,
    isLoading,
  } = params;

  // Context hooks - called directly to avoid massive param drilling
  const {
    showComparisonWithPreviousMonth, setShowComparisonWithPreviousMonth,
    showPayrollWarningModal,
    isComposing, tempNameValues,
  } = usePayrollUI();

  const {
    isLoadingActualSales, setIsLoadingActualSales,
    isLoadingPreviousMonthData, setIsLoadingPreviousMonthData,
    isSaving, setIsSaving,
    isBulkAdjustmentRunning, setIsBulkAdjustmentRunning,
    isGlobalCalculationRunning, setIsGlobalCalculationRunning,
    executingTeams, setExecutingTeams,
  } = usePayrollLoadingContext();

  const {
    underperformanceDeductionRate, setUnderperformanceDeductionRate,
    applyOvertimeMultiplier, setApplyOvertimeMultiplier,
    applyPreviousMonthAdjustment, setApplyPreviousMonthAdjustment,
    adjustmentAppliedTeams, setAdjustmentAppliedTeams,
    manualToggleTimestamp, setManualToggleTimestamp,
  } = usePayrollSettingsContext();

  const [employees, setEmployees] = useState<Employee[]>([]);
  // 지난달 직원 데이터
  const [previousMonthEmployees, setPreviousMonthEmployees] = useState<Employee[]>([]);
  // 가져온 이전 월 표시
  const [importedFromMonth, setImportedFromMonth] = useState<string | null>(null);
  // 팀별 실제매출 데이터 (Firebase에서 가져온 monthlyProfit)
  const [teamActualSales, setTeamActualSales] = useState<Record<string, number>>({});
  // 팀별 인센티브 산출 매출 계산 결과
  const [teamIncentiveCalculations, setTeamIncentiveCalculations] = useState<Record<string, TeamIncentiveCalculation>>({});

  // Refs for performance optimization and state tracking
  const isSavingRef = useRef<boolean>(false);
  const isCalculatingIncentiveRef = useRef<boolean>(false); // 🔧 인센티브 계산 중 플래그 추가
  const isAddingEmployeeRef = useRef<boolean>(false);
  const alertShownRef = useRef<Set<string>>(new Set());
  const isManualRecalculatingRef = useRef<boolean>(false); // 수동 재계산 중 추적
  // 🔧 인센티브 이펙트의 100ms 지연 목표매출 기입이 cleanup으로 파기됐을 때 '어느 월의 기입이었는지'를
  //    기억해 동일 월 재실행에서 재평가하기 위한 예약 마커 (월 전환 파기는 재예약하지 않음 — 값이 월과 다르면 무시)
  const pendingTargetSalesRecalcMonthRef = useRef<string | null>(null);

  // 지난달 미달성 금액 데이터
  const [previousMonthData, setPreviousMonthData] = useState<any>(null);

  // 🔧 원본 데이터 백업용 state 변수들 (코드 정리 시 누락되었음)
  const [originalTeams, setOriginalTeams] = useState<TeamData[]>([]);
  const [originalTargetSales, setOriginalTargetSales] = useState<Record<string, number>>({});
  
  // 📌 저장 상태 관리를 위한 새로운 state들
  // 각 필드별 변경 상태 추적 (필드ID: 변경여부)
  const [fieldChangeStatus, setFieldChangeStatus] = useState<Record<string, boolean>>({});
  // 각 필드별 저장 상태 추적 (필드ID: 'saved' | 'pending' | 'error')
  
  // 🔧 팀 삭제 후 저장을 위한 플래그
  const [pendingTeamRemoval, setPendingTeamRemoval] = useState<string | null>(null);
  const [fieldSaveStatus, setFieldSaveStatus] = useState<Record<string, 'saved' | 'pending' | 'error'>>({});
  // 저장 중인 필드들 추적
  const [savingFields, setSavingFields] = useState<Set<string>>(new Set());
  
  // 컴포넌트 언마운트 시 경고창 상태 정리
  useEffect(() => {
    return () => {
      // ESLint 경고 수정: cleanup 시점의 ref 값을 변수로 복사
      const currentRef = alertShownRef.current;
      currentRef.clear();
    };
  }, []);
  
  // 필드 변경 상태 업데이트 헬퍼 함수 (실제 값 변경 여부 확인)
  const markFieldAsChanged = (fieldId: string, newValue: any, originalValue: any) => {
    // 실제 값이 변경되었는지 확인
    const isActuallyChanged = String(newValue).replace(/,/g, '') !== String(originalValue).replace(/,/g, '');
    
    devLog(`🔍 [markFieldAsChanged] ${fieldId}:`, {
      newValue,
      originalValue,
      isActuallyChanged
    });
    
    if (!isActuallyChanged) {
      devLog(`🔍 [markFieldAsChanged] ${fieldId}: 값이 변경되지 않아 처리 스킵`);
      return;
    }
    
    devLog(`✅ [markFieldAsChanged] ${fieldId}: 필드 상태 업데이트`);
    // 값이 변경된 경우에만 변경 상태로 표시하고 저장 상태를 'pending'으로 설정
    setFieldChangeStatus(prev => ({ ...prev, [fieldId]: true }));
    setFieldSaveStatus(prev => ({ ...prev, [fieldId]: 'pending' }));
    
    // 🔧 변경사항 감지 시 즉시 isDataChanged 업데이트
    setIsDataChanged(true);
    devLog(`🚀 [markFieldAsChanged] ${fieldId}: setIsDataChanged(true) 호출 완료`);
  };
  
  // 필드 저장 완료 상태 업데이트 헬퍼 함수
  const markFieldAsSaved = (fieldId: string) => {
    setFieldChangeStatus(prev => ({ ...prev, [fieldId]: false }));
    setFieldSaveStatus(prev => ({ ...prev, [fieldId]: 'saved' }));
    setSavingFields(prev => {
      const newSet = new Set(prev);
      newSet.delete(fieldId);
      return newSet;
    });
  };
  
  // 동적 스타일 생성 함수
  const getFieldStyle = (fieldId: string) => {
    const isChanged = fieldChangeStatus[fieldId];
    const saveStatus = fieldSaveStatus[fieldId];
    const isSaving = savingFields.has(fieldId);
    
    let borderColor = '#d1d5db'; // 기본 회색
    let backgroundColor = '#ffffff'; // 기본 흰색
    
    if (isSaving) {
      borderColor = '#fbbf24'; // 저장 중 - 노란색
      backgroundColor = '#fffbeb'; // 연한 노란색 배경
    } else if (isChanged && saveStatus === 'pending') {
      borderColor = '#ef4444'; // 변경됨/저장 필요 - 빨간색
      backgroundColor = '#fef2f2'; // 연한 빨간색 배경
    } else if (saveStatus === 'saved') {
      borderColor = '#10b981'; // 저장 완료 - 초록색
      backgroundColor = '#f0fdf4'; // 연한 초록색 배경
    } else if (saveStatus === 'error') {
      borderColor = '#dc2626'; // 저장 에러 - 진한 빨간색
      backgroundColor = '#fef2f2'; // 연한 빨간색 배경
    }
    
    return {
      borderColor,
      backgroundColor,
      borderWidth: '2px',
      transition: 'all 0.3s ease'
    };
  };
  
  // 저장 상태 아이콘 컴포넌트
  const SaveStatusIcon: React.FC<{ fieldId: string; className?: string }> = ({ fieldId, className = "ml-2" }) => {
    const saveStatus = fieldSaveStatus[fieldId];
    const isSaving = savingFields.has(fieldId);
    const isChanged = fieldChangeStatus[fieldId];
    
    if (isSaving) {
      return (
        <span className={`${className} text-yellow-500`} title="저장 중...">
          <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </span>
      );
    }
    
    if (isChanged && saveStatus === 'pending') {
      return (
        <span className={`${className} text-red-500`} title="저장이 필요합니다">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </span>
      );
    }
    
    if (saveStatus === 'saved') {
      return (
        <span className={`${className} text-green-500`} title="저장 완료">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </span>
      );
    }
    
    if (saveStatus === 'error') {
      return (
        <span className={`${className} text-red-600`} title="저장 실패">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </span>
      );
    }
    
    return null;
  };
  
  // 🆕 '미지정' 팀을 정식 팀으로 처리하기 위한 헬퍼 함수
  const ensureUnassignedTeamExists = useCallback((teams: string[]): string[] => {
    const UNASSIGNED_TEAM = '미지정';
    if (!teams.includes(UNASSIGNED_TEAM)) {
      return [...teams, UNASSIGNED_TEAM];
    }
    return teams;
  }, []);

  // payroll-management 내부에서 사용할 팀 이름 목록 (string[]) - 항상 '미지정' 팀 포함
  const [localTeams, setLocalTeams] = useState<string[]>(() => ensureUnassignedTeamExists([]));
  
  // 🚀 팀 데이터 의존성 최적화
  const teamsDataLength = teamsData?.length || 0;
  const employeesLength = employees?.length || 0;
  const monthlyTeamsLength = budgetData.monthlyData?.[selectedMonth]?.teams?.length || 0;
  
  useConditionalEffect(() => {
    logDebugDetails(`${selectedMonth}월 팀 데이터 설정 시작`);
    
    // 🔧 리팩토링: TeamDataUtils 사용하여 복합 팀 데이터 생성
    const finalTeams = TeamDataUtils.generateCompositeTeamData(
      teamsData || [],
      employees,
      budgetData,
      selectedMonth
    );
    
    // 🆕 '미지정' 팀이 항상 포함되도록 보장
    const finalTeamsWithUnassigned = ensureUnassignedTeamExists(finalTeams);

    logDebugDetails(`${selectedMonth}월 최종 팀 목록`, { finalTeams: finalTeamsWithUnassigned });
    setLocalTeams(finalTeamsWithUnassigned);
  }, [
    selectedMonth,
    teamsDataLength,
    employeesLength,
    monthlyTeamsLength
  ], !isSavingRef.current && !isLoading); // 조건부 실행

  // 🔧 월별 데이터에서 지난달 미달성 조정 활성화 상태 복원
  useEffect(() => {
    const monthlyData = budgetData.monthlyData?.[selectedMonth];
    const savedState = monthlyData?.applyPreviousMonthAdjustment;
    // 이월금액이 실제로 들어가 있으면 토글은 켜진 상태로 보여야 한다.
    // 운영 DB의 실제 이월 6건은 저장된 토글 값이 없었다(undefined). 저장값만 보면 off로 시작해
    // 화면이 현실과 어긋나고, 원장이 "조정이 안 걸려 있다"고 오해한다.
    const carryoverMap = monthlyData?.teamAdjustmentCarryover;
    const hasCarryover = !!carryoverMap && Object.values(carryoverMap).some((v: any) => Number(v) > 0);
    
    // 저장된 상태가 있으면 복원, 없으면 이월 존재 여부, 둘 다 아니면 false
    const shouldApply = savedState === true || hasCarryover;
    
    setApplyPreviousMonthAdjustment(shouldApply);
    // 월 변경 시 적용 상태 초기화
    setAdjustmentAppliedTeams(new Set());
    setOriginalTargetSales({});
  }, [selectedMonth, budgetData.monthlyData]);

  // 🔧 월별 데이터에서 연장근로수당 150% 가산 상태 복원
  //    ref-guard: '월 전환' 또는 '저장된 값 자체 변경' 시에만 복원 —
  //    무관한 monthlyData 교체(급여 편집 → recalculateTeamTargetSales 깊은복사 등)가
  //    미저장 수동 토글을 되돌리지 않도록 함 (코드리뷰 BUG3)
  const lastOvertimeRestoreRef = useRef<{ month: string; savedValue: boolean | undefined } | null>(null);
  useEffect(() => {
    const monthlyData = budgetData.monthlyData?.[selectedMonth];
    const savedOvertimeMultiplier = monthlyData?.applyOvertimeMultiplier;

    const last = lastOvertimeRestoreRef.current;
    const monthChanged = !last || last.month !== selectedMonth;
    const savedValueChanged = !last || last.savedValue !== savedOvertimeMultiplier;
    if (!monthChanged && !savedValueChanged) return; // 무관한 monthlyData 변경 — 미저장 override 보존

    lastOvertimeRestoreRef.current = { month: selectedMonth, savedValue: savedOvertimeMultiplier };

    // 저장된 상태가 있으면 복원, 없으면 기본값 false (5인 이상 사업장 기준)
    const shouldApplyMultiplier = savedOvertimeMultiplier !== undefined ? savedOvertimeMultiplier : false;

    setApplyOvertimeMultiplier(shouldApplyMultiplier);
  }, [selectedMonth, budgetData.monthlyData]);

  // ⏰ 초과수당 150% 자동 판정 트리거 (인원을 바꾸는 편집에서만 true — 월 로드/전환은 위 저장값 복원 유지)
  const overtimeAutoCheckRef = useRef<boolean>(false);

  // ⏰ 파트 제외 직원 수(급여 있는 재직자) 기반 자동 토글: 직원 편집 직후에만 재판정
  //    수동 override(토글 직접 조작)는 다음 인원 편집까지 유지됨
  useEffect(() => {
    if (!overtimeAutoCheckRef.current) return;
    overtimeAutoCheckRef.current = false;
    const desired = countOvertimeEligibleEmployees(employees) >= OVERTIME_WORKPLACE_MIN_EMPLOYEES;
    if (desired === applyOvertimeMultiplier) return;
    setApplyOvertimeMultiplier(desired);
    setEmployees(prev => recalcOvertimePay(prev, desired));
    setIsDataChanged(true);
    addChangeLog(`파트 제외 직원 ${desired ? '5인 이상' : '5인 미만'} — 초과수당 150% 가산 자동 ${desired ? '활성화' : '해제'}`);
  }, [employees, applyOvertimeMultiplier]);

  // 🚀 총계 계산 메모이제이션 (성능 최적화)
  const totalCalculations = useMemo(() => {
    const totalIncentive = employees.reduce((sum, emp) => sum + emp.incentive, 0);

    return {
      totalIncentive
    };
  }, [employees]); // employees 배열이 변경될 때만 재계산

  const {
    totalIncentive
  } = totalCalculations;
  
  // 🔧 월별 데이터에서 팀별 목표매출은 아래 useMemo에서 계산됨
  

  // 상태 변경 추적 (자동 저장 플래그용 - 필요시 AppContext의 lastSaveTime으로 대체 가능)
  const [isDataChanged, setIsDataChanged] = useState(false);
  const [changeLog, setChangeLog] = useState<string[]>([]);
  
  // isDataChanged 상태 변경 감지를 위한 useEffect 추가
  useEffect(() => {
  }, [isDataChanged, selectedMonth, changeLog]);
  
  // 페이지 이탈 시 미저장 변경사항 경고
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDataChanged) {
        e.preventDefault();
        e.returnValue = '저장되지 않은 변경사항이 있습니다. 정말 나가시겠습니까?';
        return '저장되지 않은 변경사항이 있습니다. 정말 나가시겠습니까?';
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDataChanged]);
  
  // 🔧 fieldChangeStatus 변경 감지하여 isDataChanged 자동 동기화 (백업 안전장치)
  useEffect(() => {
    const hasChangedFields = Object.values(fieldChangeStatus).some(changed => changed);
    
    // fieldChangeStatus에 변경된 필드가 있는데 isDataChanged가 false인 경우 동기화
    if (hasChangedFields && !isDataChanged) {
      setIsDataChanged(true);
    }
  }, [fieldChangeStatus, isDataChanged]);
  
  // 원본 데이터 복사본 저장을 위한 상태 변수
  const [originalEmployees, setOriginalEmployees] = useState<Employee[]>([]);
  const [originalBudgetData, setOriginalBudgetData] = useState<any>(null);
  
  
  // 🔧 월 변경 시 원본 데이터 초기화 — '실제 월 변경'에만 반응 (ref 비교)
  //    ⚠️ budgetData·직원 수를 deps에 두면 편집/로드 때마다 setIsDataChanged(false)가 실행되어
  //    dirty 플래그가 수시로 소거 → 미저장 경고·월 전환 저장 프롬프트가 무력화되므로 deps를 축소한다.
  const prevOriginalResetMonthRef = useRef<string>(selectedMonth);
  useEffect(() => {
    if (prevOriginalResetMonthRef.current === selectedMonth) return; // 월 변경이 아니면 dirty 보존
    prevOriginalResetMonthRef.current = selectedMonth;

    logDataOperation('월 변경 감지 - 원본 데이터 초기화', { selectedMonth });
    setOriginalEmployees([]);
    setOriginalTeams([]);
    setOriginalBudgetData(null);
    setIsDataChanged(false);
  }, [selectedMonth]);

  // 🔧 전체적용 인건비율 가중평균 자동 재계산 (위 '월 변경 감지' 이펙트에서 분리 — 기존 동작 보존:
  //    데이터 로드/편집으로 deps가 바뀔 때마다, 해당 월에 인건비율 미설정 시에만 가중평균을 설정)
  useEffect(() => {
    if (localTeams.length > 0 && employees.length > 0) {
      // 약간의 지연을 두어 데이터 로드 완료 보장
      setTimeout(() => {
        if (!budgetData.monthlyData?.[selectedMonth]?.personnelPercentage) {
          const weightedAvg = calculateWeightedAveragePersonnelPercentage(
            localTeams,
            employees,
            selectedMonth,
            budgetData
          );

          logDataOperation('월 변경 시 가중평균 인건비율 자동 설정', {
            새로운월: selectedMonth,
            계산된가중평균: weightedAvg.toFixed(2) + '%',
            팀수: localTeams.length,
            직원수: employees.length
          });

          // budgetData 업데이트
          setBudgetData(prev => ({
            ...prev,
            monthlyData: {
              ...prev.monthlyData,
              [selectedMonth]: {
                ...prev.monthlyData?.[selectedMonth],
                personnelPercentage: weightedAvg
              }
            }
          }));
        }
      }, 500); // 데이터 로드를 위한 짧은 지연
    }
  }, [selectedMonth, localTeams.length, employees.length, budgetData, setBudgetData]);

  // 변경사항 로그 추가 함수 - useCallback 최적화
  const addChangeLog = useCallback((change: string) => {
    logDataOperation('변경사항 로그 추가', { change, previousLogCount: changeLog.length });
    setChangeLog(prev => [...prev, change]);
    setIsDataChanged(true);
  }, [changeLog.length]);
  
  // 처음 로드될 때 및 저장 후 원본 데이터 저장 - 최적화된 useEffect
  useEffect(() => {
    logPerformanceCritical('원본 데이터 설정 useEffect 실행', {
      selectedMonth,
      isLoading,
      employeesLength: employees.length,
      teamsDataLength: teamsData.length,
      localTeamsLength: localTeams.length,
      hasMonthlyData: !!budgetData.monthlyData?.[selectedMonth],
      hasOriginalData: !!originalBudgetData
    });

    // 저장 중이면 실행하지 않음
    if (isSavingRef.current) {
      logDebugDetails('원본 데이터 설정 스킵 - 저장 중');
      return;
    }

    // 디바운스 처리로 빈번한 실행 방지
    const timeoutId = setTimeout(() => {
      // 🔧 모든 데이터가 완전히 로드되고, localTeams도 설정된 후에만 원본 데이터 설정
      if (!isLoading && 
          employees.length >= 0 &&  // 0 이상으로 변경 (빈 데이터도 허용)
        teamsData.length > 0 && 
        localTeams.length > 0 && // localTeams도 설정되었는지 확인
        budgetData && 
        Object.keys(budgetData).length > 0 &&
        !originalBudgetData) {
      
        logDataOperation('원본 데이터 설정 시작', {
          employeesLength: employees.length,
          teamsLength: teamsData.length,
          localTeamsLength: localTeams.length,
          localTeams: localTeams
        });
        
        setOriginalEmployees(JSON.parse(JSON.stringify(employees)));
        setOriginalTeams(JSON.parse(JSON.stringify(teamsData)));
        setOriginalBudgetData(JSON.parse(JSON.stringify(budgetData)));
        
        logDataOperation('원본 데이터 설정 완료', {
          originalTeamNames: teamsData.map(t => t.name),
          currentLocalTeams: localTeams
        });
        
        // 🔧 페이지 로드 시 전체적용 인건비율 자동 설정
        // 월별 데이터에 인건비율이 설정되지 않은 경우에만 가중평균 계산 및 설정
        if (!budgetData.monthlyData?.[selectedMonth]?.personnelPercentage && localTeams.length > 0) {
          const weightedAvg = calculateWeightedAveragePersonnelPercentage(
            localTeams,
            employees,
            selectedMonth,
            budgetData
          );
          
          logDataOperation('페이지 로드 시 가중평균 인건비율 자동 설정', {
            월: selectedMonth,
            계산된가중평균: weightedAvg.toFixed(2) + '%',
            팀수: localTeams.length,
            직원수: employees.length
          });
          
          // budgetData 업데이트
          setBudgetData(prev => ({
            ...prev,
            monthlyData: {
              ...prev.monthlyData,
              [selectedMonth]: {
                ...prev.monthlyData?.[selectedMonth],
                personnelPercentage: weightedAvg
              }
            }
          }));
        }
      }
    }, 2000); // 2초 디바운스로 증가 (localTeams 설정 대기)

    // 클린업 함수로 타이머 정리
    return () => {
      clearTimeout(timeoutId);
    };
  }, [
    selectedMonth,
    isLoading,
    employees.length,
    teamsData.length,
    localTeams.length, // localTeams 길이도 의존성에 추가
    // budgetData 전체가 아닌 필요한 부분만 의존성으로 사용
    !!budgetData.monthlyData?.[selectedMonth],
    !!originalBudgetData,
    setBudgetData
  ]); // 🔧 의존성 최적화 완료
  
  // 중복 감지 및 자동 통합 기능이 제거되었습니다.

  const [checkedItems, setCheckedItems] = useState<{[key: string | number]: boolean}>({});
  const [allChecked, setAllChecked] = useState(false);
  const [teamSortType, setTeamSortType] = useState<SortType>('numeric');
  const [lastSaveTime, setLastSaveTime] = useState<string>(''); // 컴포넌트 내부 마지막 저장 시간

  // 🔧 팀 삭제 후 budgetData 업데이트 감지 및 자동 저장
  useEffect(() => {
    if (pendingTeamRemoval && !isSaving) {
      
      // 디버깅: 현재 budgetData 상태 확인
      
      // Firebase teams 컬렉션에서 팀 삭제
      const teamToDelete = teamsData.find(team => team.name === pendingTeamRemoval);
      if (teamToDelete && teamToDelete.id) {
        deleteTeam(teamToDelete.id).then(() => {
          logTeamOperation('Firebase teams 컬렉션에서 삭제 완료', teamToDelete.id);
        }).catch(error => {
          logError('Firebase teams 컬렉션에서 팀 삭제', error, { teamId: teamToDelete.id });
        });
      }
      
      // 저장 실행
      internalSaveAllData().then(() => {
        setPendingTeamRemoval(null);
        
        // 저장 후 Firebase 데이터 검증
        setTimeout(async () => {
          
          const latestBudgetData = await getBudgetData();
          
          if (latestBudgetData) {
            // 팀 관련 데이터가 완전히 삭제되었는지 확인
            const verificationResults: Record<string, boolean> = {
              '전역 teamTargetSales': latestBudgetData.teamTargetSales?.[pendingTeamRemoval] === undefined,
              '전역 teamPersonnelPercentages': latestBudgetData.teamPersonnelPercentages?.[pendingTeamRemoval] === undefined,
              '전역 teamIncentiveRatios': latestBudgetData.teamIncentiveRatios?.[pendingTeamRemoval] === undefined,
              '전역 teamSalaryTotals': latestBudgetData.teamSalaryTotals?.[pendingTeamRemoval] === undefined,
              '전역 teamIncentiveCalculations': latestBudgetData.teamIncentiveCalculations?.[pendingTeamRemoval] === undefined,
              '전역 teamPureTargetSales': latestBudgetData.teamPureTargetSales?.[pendingTeamRemoval] === undefined,
              '전역 teamPerformanceData': latestBudgetData.teamPerformanceData?.[pendingTeamRemoval] === undefined
            };
            
            // 모든 월별 데이터 확인
            if (latestBudgetData.monthlyData) {
              Object.keys(latestBudgetData.monthlyData).forEach(month => {
                const monthData = latestBudgetData.monthlyData[month];
                verificationResults[`${month} teams 배열`] = !monthData.teams?.includes(pendingTeamRemoval);
                verificationResults[`${month} teamTargetSales`] = monthData.teamTargetSales?.[pendingTeamRemoval] === undefined;
                verificationResults[`${month} teamPersonnelPercentages`] = monthData.teamPersonnelPercentages?.[pendingTeamRemoval] === undefined;
                verificationResults[`${month} teamIncentiveRatios`] = monthData.teamIncentiveRatios?.[pendingTeamRemoval] === undefined;
                verificationResults[`${month} teamSalaryTotals`] = monthData.teamSalaryTotals?.[pendingTeamRemoval] === undefined;
                verificationResults[`${month} teamIncentiveCalculations`] = monthData.teamIncentiveCalculations?.[pendingTeamRemoval] === undefined;
                verificationResults[`${month} teamPerformanceData`] = monthData.teamPerformanceData?.[pendingTeamRemoval] === undefined;
                verificationResults[`${month} teamPureTargetSales`] = monthData.teamPureTargetSales?.[pendingTeamRemoval] === undefined;
              });
            }
            
            // 검증 결과 출력
            const failedChecks = Object.entries(verificationResults).filter(([key, passed]) => !passed);
            
            if (failedChecks.length === 0) {
            } else {
              console.error(`❌ [팀 삭제 검증] 팀 "${pendingTeamRemoval}" 일부 데이터가 삭제되지 않음:`, failedChecks.map(([key]) => key));
            }
          }
        }, 2000);
      }).catch(error => {
        console.error(`팀 "${pendingTeamRemoval}" 삭제 저장 실패:`, error);
        alert(`팀 삭제 저장에 실패했습니다: ${error.message}`);
        setPendingTeamRemoval(null);
      });
    }
  }, [pendingTeamRemoval, isSaving, teamsData, selectedMonth]); // budgetData 변경 감지

  // 🔧 순수목표매출이 0인 팀들을 자동 계산하는 useEffect
  useEffect(() => {
    // 🚨 수동 재계산 중이면 자동 재계산 방지
    if (isManualRecalculatingRef.current) {
      return;
    }
    
    // 모든 팀을 확인하여 순수목표매출이 0이지만 인건비와 인건비율이 있는 팀 찾기
    const teamsToRecalculate: string[] = [];
    
    localTeams.forEach(teamName => {
      const teamEmployees = employees.filter(emp => emp.team === teamName);
      const teamSalaryTotal = calculateSalaryTotal(teamEmployees);
      
      const currentMonthData = getSafeMonthlyData(budgetData, selectedMonth);
      const teamPersonnelPercentage = currentMonthData.teamPersonnelPercentages?.[teamName] || 
                                     budgetData.teamPersonnelPercentages?.[teamName] || 
                                     currentMonthData.personnelPercentage || 
                                     budgetData.personnelPercentage || 19;

      const currentPureTargetSales = currentMonthData.teamPureTargetSales?.[teamName] || 
                                    budgetData.teamPureTargetSales?.[teamName] || 0;
      
      // 인건비합계와 인건비율이 0이 아니지만 순수목표매출이 0인 경우
      if (teamName !== '미지정' && teamSalaryTotal > 0 && teamPersonnelPercentage > 0 && currentPureTargetSales === 0) {
        teamsToRecalculate.push(teamName);
      }
    });
    
    // 재계산이 필요한 팀들이 있으면 순차적으로 계산
    if (teamsToRecalculate.length > 0) {
      
      // 각 팀에 대해 순차적으로 계산 실행
      teamsToRecalculate.forEach((teamName, index) => {
        setTimeout(() => {
          recalculateTeamTargetSales(teamName, employees);
        }, 300 * (index + 1)); // 각 팀마다 300ms 간격으로 실행
      });
    }
  }, [
    employees.length, 
    employees.reduce((sum, emp) => sum + emp.salary, 0), // 급여 총합으로 급여 변경 감지
    localTeams, 
    selectedMonth, 
    budgetData.personnelPercentage
  ]); // 🔧 수정: employees 배열 참조 대신 length와 급여총합 사용하여 무한루프 방지

  // 팀 정렬 함수 - useCallback 최적화
  const getSortedTeams = useCallback((teamsToSort: string[], sortTypeToUse: SortType): string[] => {
    const sorted = [...teamsToSort];
    switch(sortTypeToUse) {
      case 'numeric':
        return sorted.sort((a, b) => {
          const numA = parseInt(a.replace(/[^0-9]/g, '')) || 0;
          const numB = parseInt(b.replace(/[^0-9]/g, '')) || 0;
          return numA - numB;
        });
      case 'alphabetic':
        return sorted.sort((a, b) => a.localeCompare(b, 'en', { sensitivity: 'base' }));
      case 'korean':
        return sorted.sort((a, b) => a.localeCompare(b, 'ko'));
      default:
        return sorted;
    }
  }, []);

  // ===== Sub-hooks called internally (resolves circular dependency) =====
  const {
    teamAdjustmentCache,
    calculateIncomeTax,
    calculateIncentiveCalculationSales,
    calculateWeightedAveragePersonnelPercentage,
    getEffectivePersonnelPercentage,
    calculateDeductions,
    calculateNetSalaryWithAllowances,
    calculateRealTimeIndividualIncentive,
    recalculateTeamTargetSales,
    calculateFinalTargetSales,
    checkTeamAndLeaderMatch,
    calculatePreviousMonthUnderperformance,
    getCachedAdjustmentInfo,
    updateUnderperformanceDeductionRate: updateUnderperformanceDeductionRateFromCalc,
    logIfChanged,
  } = usePayrollCalculations({
    employees,
    budgetData,
    selectedMonth,
    localTeams,
    teamActualSales,
    underperformanceDeductionRate,
    applyPreviousMonthAdjustment,
    previousMonthData,
    adjustmentAppliedTeams,
    executingTeams,
    teamSortType,
    setBudgetData,
    setExecutingTeams,
    setAdjustmentAppliedTeams,
    setUnderperformanceDeductionRate,
    setIsDataChanged,
    getSortedTeams,
    addChangeLog,
  });

  const {
    lastLoadedMonthRef,
    isLoadingDataRef,
    loadedMonth,
    handleImportPreviousMonth,
    loadPreviousMonthData,
    restoreOriginalTargetSales,
    applyAdjustmentToAllEligibleTeams,
    handleAdjustmentToggle,
  } = useMonthlyData({
    selectedMonth,
    budgetData,
    employees,
    globalEmployees,
    localTeams,
    previousMonthData,
    showComparisonWithPreviousMonth,
    importedFromMonth,
    applyPreviousMonthAdjustment,
    adjustmentAppliedTeams,
    originalTargetSales,
    isLoadingPreviousMonthData,
    isBulkAdjustmentRunning,
    manualToggleTimestamp,
    underperformanceDeductionRate,
    isSavingRef,
    isAddingEmployeeRef,
    setBudgetData,
    setEmployees,
    setPreviousMonthData,
    setPreviousMonthEmployees,
    setShowComparisonWithPreviousMonth,
    setApplyPreviousMonthAdjustment,
    setAdjustmentAppliedTeams,
    setOriginalTargetSales,
    setOriginalEmployees,
    setOriginalTeams,
    setOriginalBudgetData,
    setIsLoadingPreviousMonthData,
    setIsBulkAdjustmentRunning,
    setManualToggleTimestamp,
    setUnderperformanceDeductionRate,
    setImportedFromMonth,
    setIsDataChanged,
    setChangeLog,
    addChangeLog,
    calculateFinalTargetSales,
  });

  const addTeam = useCallback(() => {
    const teamName = prompt("새 팀 이름을 입력하세요:");
    if (!teamName || teamName.trim() === '') return;
    
    logUserAction('팀 추가 시작', { teamName, month: selectedMonth });
    
    // 현재 월의 팀 목록에서 중복 검사
    const currentMonthTeams = budgetData.monthlyData?.[selectedMonth]?.teams || localTeams;
    if (currentMonthTeams.includes(teamName)) {
      alert(`${selectedMonth}월에 이미 존재하는 팀 이름입니다.`);
      return;
    }
    
    // 로컬 상태 먼저 업데이트 ('미지정' 팀 항상 포함)
    setLocalTeams(prev => ensureUnassignedTeamExists([...prev, teamName]));
    
    // 월별 데이터에 팀 정보 저장
    setBudgetData(prev => {
      // 현재 월의 기존 데이터 가져오기
      const currentMonthData = getSafeMonthlyData(prev, selectedMonth);
      const currentTeams = currentMonthData.teams || localTeams;
      const currentTeamTargetSales = currentMonthData.teamTargetSales || {};
      const currentTeamPersonnelPercentages = currentMonthData.teamPersonnelPercentages || {};
      const currentTeamIncentiveRatios = currentMonthData.teamIncentiveRatios || {};
      
      // 새 팀 추가
      const newTeams = [...currentTeams, teamName];
      const newTeamTargetSales = {...currentTeamTargetSales, [teamName]: 0};
      const newTeamPersonnelPercentages = {...currentTeamPersonnelPercentages, [teamName]: prev.personnelPercentage || 19};
      const newTeamIncentiveRatios = {
        ...currentTeamIncentiveRatios,
        [teamName]: {
          performance: prev.incentiveRatios.performance,
          team: prev.incentiveRatios.team,
          member: prev.incentiveRatios.member
        }
      };
      
      // 월별 데이터 업데이트 - 완전한 깊은 복사로 다른 월 독립성 보장
      const updatedMonthlyData = JSON.parse(JSON.stringify(prev.monthlyData || {}));
      updatedMonthlyData[selectedMonth] = {
        ...currentMonthData, // 🔒 월별 전용 필드(underperformanceDeductionRate·employees·importedFromMonth 등) 보존 - removeTeam과 동일 패턴
        // 이 작업이 실제로 바꾸는 필드만 override (spread가 이미 제공하는 값의 재나열 금지 - 드리프트 방지)
        targetSales: Object.values(newTeamTargetSales).reduce((sum: number, val) => sum + (Number(val) || 0), 0),
        teamTargetSales: newTeamTargetSales,
        teamPersonnelPercentages: newTeamPersonnelPercentages,
        teamIncentiveRatios: newTeamIncentiveRatios,
        teams: newTeams,
        lastUpdated: new Date().toISOString()
      };
      
      logTeamOperation('팀 추가 완료', teamName, { month: selectedMonth, personnelPercentage: prev.personnelPercentage || 19 });
      
      // 현재 월인 경우에만 전역 데이터도 업데이트
      const currentYearMonth = new Date().toISOString().slice(0, 7);
      if (selectedMonth === currentYearMonth) {
    // AppContext 팀 데이터 업데이트 (ID 생성)
    if (teamsData && Array.isArray(teamsData)) {
      const newTeamId = `team_${Date.now()}`;
      const newTeam: TeamData = {
        id: newTeamId,
        name: teamName,
        targetSales: 0
      };
      setTeamsContext(prev => Array.isArray(prev) ? [...prev, newTeam] : [newTeam]);
        }
        
        return {
          ...prev,
          monthlyData: updatedMonthlyData,
          teamTargetSales: {...prev.teamTargetSales, [teamName]: 0},
          teamIncentiveRatios: {...prev.teamIncentiveRatios, [teamName]: newTeamIncentiveRatios[teamName]},
          teamPersonnelPercentages: {...prev.teamPersonnelPercentages, [teamName]: prev.personnelPercentage || 19}
        };
      } else {
        // 현재 월이 아닌 경우 월별 데이터만 업데이트
        return {
          ...prev,
          monthlyData: updatedMonthlyData
        };
    }
    });
    
    addChangeLog(`${selectedMonth}월 팀 추가: ${teamName}`);
  // 🔧 FIX C10-D1: JSON.stringify 의존성 제거 - budgetData 참조로 변경 감지
  }, [budgetData, selectedMonth, localTeams, teamsData, setLocalTeams, setBudgetData, setTeamsContext, addChangeLog]);

  const removeTeam = (teamNameToRemove: string) => {
    // 🆕 '미지정' 팀은 삭제할 수 없도록 보호
    if (teamNameToRemove === '미지정') {
      alert('미지정 팀은 시스템에서 사용하는 기본 팀으로 삭제할 수 없습니다.');
      return;
    }

    if (!window.confirm(`${selectedMonth}월에서 '${teamNameToRemove}' 팀을 삭제하시겠습니까?`)) {
      return;
    }

    // 팀에 속한 직원 확인
    const employeesInTeam = employees.filter(emp => emp.team === teamNameToRemove);
    if (employeesInTeam.length > 0) {
      if (!window.confirm(`이 팀에는 ${employeesInTeam.length}명의 직원이 속해 있습니다. 삭제하면 이 직원들의 팀은 '미지정'으로 변경됩니다. 계속하시겠습니까?`)) {
        return;
      }
      
      // 직원 팀 업데이트
      setEmployees(prev => prev.map(emp => {
        if (emp.team === teamNameToRemove) {
          return {...emp, team: '미지정'};
        }
        return emp;
      }));
    }
    
    // 로컬 상태 업데이트 ('미지정' 팀은 항상 유지)
    setLocalTeams(prev => ensureUnassignedTeamExists(prev.filter(t => t !== teamNameToRemove)));
    
    // 🔧 월별 데이터에서 팀 정보 삭제
      setBudgetData(prev => {
      // 현재 월의 기존 데이터 가져오기
      const currentMonthData = getSafeMonthlyData(prev, selectedMonth);
      
      // 팀 삭제
      const newTeams = (currentMonthData.teams || localTeams).filter(t => t !== teamNameToRemove);
      const newTeamTargetSales = {...(currentMonthData.teamTargetSales || {})};
        delete newTeamTargetSales[teamNameToRemove];
        
      const newTeamPersonnelPercentages = {...(currentMonthData.teamPersonnelPercentages || {})};
      delete newTeamPersonnelPercentages[teamNameToRemove];
      
      const newTeamIncentiveRatios = {...(currentMonthData.teamIncentiveRatios || {})};
        delete newTeamIncentiveRatios[teamNameToRemove];
        
      // 🔧 누락된 팀 관련 데이터 삭제 추가
      const newTeamSalaryTotals = {...(currentMonthData.teamSalaryTotals || {})};
      delete newTeamSalaryTotals[teamNameToRemove];
      
      const newTeamIncentiveCalculations = {...(currentMonthData.teamIncentiveCalculations || {})};
      delete newTeamIncentiveCalculations[teamNameToRemove];
      
      const newTeamPerformanceData = {...(currentMonthData.teamPerformanceData || {})};
      delete newTeamPerformanceData[teamNameToRemove];
      
      // teamPureTargetSales 삭제 추가
      const newTeamPureTargetSales = {...(currentMonthData.teamPureTargetSales || {})};
      delete newTeamPureTargetSales[teamNameToRemove];
      // 이월금액·플래그도 함께 제거 — 목표와 같은 맵 집합으로 다룬다
      const newTeamCarryover = {...(currentMonthData.teamAdjustmentCarryover || {})};
      delete newTeamCarryover[teamNameToRemove];
      const newAdjustmentApplied = {...(currentMonthData.adjustmentApplied || {})};
      delete newAdjustmentApplied[teamNameToRemove];
        
      // 월별 데이터 업데이트 - 완전한 깊은 복사로 다른 월 독립성 보장
      const updatedMonthlyData = JSON.parse(JSON.stringify(prev.monthlyData || {}));
      updatedMonthlyData[selectedMonth] = {
        // 기존 월별 데이터 유지하면서 이 작업이 실제로 바꾸는 필드(키 제거된 팀맵들)만 override
        ...(currentMonthData || {}),
        targetSales: Object.values(newTeamTargetSales).reduce((sum: number, val) => sum + (Number(val) || 0), 0),
        teamTargetSales: newTeamTargetSales,
        teamAdjustmentCarryover: newTeamCarryover,
        adjustmentApplied: newAdjustmentApplied,
        teamPersonnelPercentages: newTeamPersonnelPercentages,
        teamSalaryTotals: newTeamSalaryTotals,
        teamIncentiveRatios: newTeamIncentiveRatios,
        teamIncentiveCalculations: newTeamIncentiveCalculations,
        teamPerformanceData: newTeamPerformanceData,
        teamPureTargetSales: newTeamPureTargetSales,
        teams: newTeams,
        lastUpdated: new Date().toISOString()
      };
      
      // 🔧 모든 월별 데이터에서 팀 삭제 - 이미 깊은 복사된 상태
      const cleanedMonthlyData = updatedMonthlyData;
      Object.keys(cleanedMonthlyData).forEach(month => {
        if (month !== selectedMonth && cleanedMonthlyData[month]) {
          // 다른 월에서도 팀 관련 데이터 삭제
          if (cleanedMonthlyData[month].teams) {
            cleanedMonthlyData[month].teams = cleanedMonthlyData[month].teams.filter((t: string) => t !== teamNameToRemove);
          }
          if (cleanedMonthlyData[month].teamTargetSales) {
            delete cleanedMonthlyData[month].teamTargetSales[teamNameToRemove];
          }
          if (cleanedMonthlyData[month].teamPersonnelPercentages) {
            delete cleanedMonthlyData[month].teamPersonnelPercentages[teamNameToRemove];
          }
          if (cleanedMonthlyData[month].teamIncentiveRatios) {
            delete cleanedMonthlyData[month].teamIncentiveRatios[teamNameToRemove];
          }
          if (cleanedMonthlyData[month].teamSalaryTotals) {
            delete cleanedMonthlyData[month].teamSalaryTotals[teamNameToRemove];
          }
          if (cleanedMonthlyData[month].teamIncentiveCalculations) {
            delete cleanedMonthlyData[month].teamIncentiveCalculations[teamNameToRemove];
          }
          if (cleanedMonthlyData[month].teamPerformanceData) {
            delete cleanedMonthlyData[month].teamPerformanceData[teamNameToRemove];
          }
          if (cleanedMonthlyData[month].teamPureTargetSales) {
            delete cleanedMonthlyData[month].teamPureTargetSales[teamNameToRemove];
          }
        }
      });
      
      // 🔧 현재 월인 경우에만 전역 데이터도 업데이트
      const currentYearMonth = new Date().toISOString().slice(0, 7);
      if (selectedMonth === currentYearMonth) {
        // AppContext 팀 데이터에서 삭제
        if (teamsData && Array.isArray(teamsData)) {
          setTeamsContext(prev => Array.isArray(prev) ? prev.filter(team => team && team.name !== teamNameToRemove) : []);
    }
    
        return {
          ...prev,
          monthlyData: cleanedMonthlyData,
          teamTargetSales: (() => {
            const newGlobalTeamTargetSales = {...prev.teamTargetSales};
            delete newGlobalTeamTargetSales[teamNameToRemove];
            return newGlobalTeamTargetSales;
          })(),
          teamIncentiveRatios: (() => {
            const newGlobalTeamIncentiveRatios = {...prev.teamIncentiveRatios};
            delete newGlobalTeamIncentiveRatios[teamNameToRemove];
            return newGlobalTeamIncentiveRatios;
          })(),
          teamPersonnelPercentages: (() => {
            const newGlobalTeamPersonnelPercentages = {...prev.teamPersonnelPercentages};
            delete newGlobalTeamPersonnelPercentages[teamNameToRemove];
            return newGlobalTeamPersonnelPercentages;
          })(),
          teamSalaryTotals: (() => {
            const newGlobalTeamSalaryTotals = {...(prev.teamSalaryTotals || {})};
            delete newGlobalTeamSalaryTotals[teamNameToRemove];
            return newGlobalTeamSalaryTotals;
          })(),
          teamIncentiveCalculations: (() => {
            const newGlobalTeamIncentiveCalculations = {...(prev.teamIncentiveCalculations || {})};
            delete newGlobalTeamIncentiveCalculations[teamNameToRemove];
            return newGlobalTeamIncentiveCalculations;
          })(),
          teamPureTargetSales: (() => {
            const newGlobalTeamPureTargetSales = {...(prev.teamPureTargetSales || {})};
            delete newGlobalTeamPureTargetSales[teamNameToRemove];
            return newGlobalTeamPureTargetSales;
          })(),
          teamPerformanceData: (() => {
            const newGlobalTeamPerformanceData = {...(prev.teamPerformanceData || {})};
            delete newGlobalTeamPerformanceData[teamNameToRemove];
            return newGlobalTeamPerformanceData;
          })()
        };
      } else {
        // 현재 월이 아닌 경우 월별 데이터만 업데이트
        return {
          ...prev,
          monthlyData: cleanedMonthlyData
        };
      }
    });
    
    addChangeLog(`${selectedMonth}월 팀 삭제: ${teamNameToRemove}`);
    
    // 🔧 누락된 로컬 state에서도 팀 관련 데이터 삭제
    // teamActualSales에서 삭제
    setTeamActualSales(prev => {
      const newTeamActualSales = {...prev};
      delete newTeamActualSales[teamNameToRemove];
      return newTeamActualSales;
    });
    
    // teamIncentiveCalculations에서 삭제
    setTeamIncentiveCalculations(prev => {
      const newCalculations = {...prev};
      delete newCalculations[teamNameToRemove];
      return newCalculations;
    });
    
    // adjustmentAppliedTeams에서 삭제
    setAdjustmentAppliedTeams(prev => {
      const newSet = new Set(prev);
      newSet.delete(teamNameToRemove);
      return newSet;
    });
    
    // 데이터 변경 플래그 설정
    setIsDataChanged(true);
    
    // 팀 삭제 플래그 설정 - useEffect에서 budgetData 업데이트 감지 후 저장
    setPendingTeamRemoval(teamNameToRemove);
  };
  
  const updateEmployeeTeam = (id: number | string, team: string) => {
    // 변경될 직원 찾기
    const employee = employees.find(emp => emp.id === id);
    if (!employee) return;
    
    const employeeName = employee.name;
    const previousTeam = employee.team;
    const currentPosition = employee.position;
    
    // 팀장이 다른 팀으로 이동하는 경우 중복 확인
    if (currentPosition === '팀장' && team !== previousTeam) {
      // 이동할 팀에 이미 팀장이 있는지 확인
      const existingLeaderInTargetTeam = employees.find(emp => 
        emp.team === team && 
        emp.position === '팀장' && 
        emp.id !== id
      );
      
      if (existingLeaderInTargetTeam) {
        // 🔧 경고창 중복 방지 로직 수정
        const alertKey = `team_${team}_${id}_${existingLeaderInTargetTeam.id}`;
        
        // 이미 처리 중인 경우 중복 실행 방지
        if (alertShownRef.current.has(alertKey)) {
          return;
        }
        
        // 처리 시작 표시
        alertShownRef.current.add(alertKey);
        
        // 경고창 표시
        const confirmMessage = `${team}팀에 이미 팀장 '${existingLeaderInTargetTeam.name}'이 있습니다.\n\n` +
                             `팀장 '${employeeName}'이(가) ${team}으로 이동하면:\n` +
                             `• 기존 ${team} 팀장 '${existingLeaderInTargetTeam.name}' → 팀원으로 변경\n` +
                             `• '${employeeName}' → ${team}의 새로운 팀장\n\n` +
                             `계속하시겠습니까?`;

        // 경고창 결과에 따른 처리 — 처리 완료 후 동일 조합 재시도 허용 (키 영구 보존 시 취소 후 재시도 차단, BUG1 형제)
        const confirmed = window.confirm(confirmMessage);
        setTimeout(() => { alertShownRef.current.delete(alertKey); }, 0);
        if (confirmed) {
          // 사용자가 확인하면 이동할 팀의 기존 팀장을 팀원으로 변경
          addChangeLog(`팀장 팀 이동: ${employeeName}(${previousTeam}→${team}), ${existingLeaderInTargetTeam.name}(팀장→팀원)`);
          
          // 이동할 팀의 기존 팀장을 팀원으로 변경
          setEmployees(prevEmployees => 
            prevEmployees.map(emp => {
              if (emp.id === existingLeaderInTargetTeam.id) {
                return { ...emp, position: '팀원' };
              }
              if (emp.id === id) {
                return { ...emp, team };
              }
              return emp;
            })
          );
        }
      } else {
        // 이동할 팀에 팀장이 없으면 그냥 이동
        setEmployees(prevEmployees => 
          prevEmployees.map(emp => emp.id === id ? { ...emp, team } : emp)
        );
        addChangeLog(`팀장 팀 이동: ${employeeName}(${previousTeam}→${team})`);
      }
    } else {
      // 팀장이 아니거나 같은 팀 내에서의 변경인 경우 그냥 이동
    setEmployees(prevEmployees => 
      prevEmployees.map(emp => emp.id === id ? { ...emp, team } : emp)
    );
    
      if (team !== previousTeam) {
        addChangeLog(`팀 변경: ${employeeName}(${currentPosition}) ${previousTeam}→${team}`);
      }
    }
    
    // 🔧 데이터 변경 플래그 설정
    setIsDataChanged(true);
  };

  
  const updateNonTaxable = (id: number | string, value: string) => {
    const fieldId = `nonTaxable_${id}`;
    const numValue = Number(value.replace(/,/g, '')) || 0;
    const originalValue = getOriginalValue(fieldId, id);
    
    markFieldAsChanged(fieldId, numValue, originalValue);
    
    // 로컬 월별 직원 데이터만 업데이트
    setEmployees(prev => 
      prev.map(emp => {
        if (emp.id === id) {
          const deductions = calculateDeductions({...emp, nonTaxable: numValue});
          return {...emp, nonTaxable: numValue, tax: deductions.tax, netSalary: deductions.netSalary};
        }
        return emp;
      })
    );
    setIsDataChanged(true);
  };

  const updateEmployeeSalary = (id: number | string, value: string) => {
    
    const fieldId = `salary_${id}`;
    const numValue = Number(value.replace(/,/g, '')) || 0;
    const originalValue = getOriginalValue(fieldId, id);
    
    markFieldAsChanged(fieldId, numValue, originalValue);
    
    // 🔧 해당 직원의 팀 정보 미리 저장 (재계산을 위해)
    const targetEmployee = employees.find(emp => emp.id === id);
    const targetTeam = targetEmployee?.team;
    
    // 로컬 월별 직원 데이터만 업데이트
    const updatedEmployees = employees.map(emp => {
      if (emp.id === id) {
        const deductions = calculateDeductions({...emp, salary: numValue});
        const retirement = resolveRetirementPay({ ...emp, salary: numValue });
        return {
          ...emp, 
          salary: numValue, 
          tax: deductions.tax, 
          netSalary: deductions.netSalary,
          retirement
        };
      }
      return emp;
    });
    
    setEmployees(updatedEmployees);
    overtimeAutoCheckRef.current = true; // ⏰ 급여 0↔양수 교차 시 5인 카운트 변동 가능 → 자동 판정
    setIsDataChanged(true);

    // 🚀 급여 변경 시 해당 팀의 목표매출 즉시 재계산 (실제 변경된 경우에만)
    if (targetTeam && numValue !== originalValue) {
      
      // 🚀 수정: 수동 재계산 플래그 간소화 - 연속 수정 지원
      isManualRecalculatingRef.current = true;
      
      // 최신 직원 데이터로 즉시 재계산
      recalculateTeamTargetSales(targetTeam, updatedEmployees);
      
      // 🚀 수정: 100ms 후 즉시 재활성화 (연속 수정 가능)
      setTimeout(() => {
        isManualRecalculatingRef.current = false;
      }, 100);
    }
  };
  
  const updateEmployeeFamilyCount = (id: number | string, value: string) => {
    const fieldId = `familyCount_${id}`;
    const numValue = Math.max(0, Number(value) || 0);
    const originalValue = getOriginalValue(fieldId, id);
    
    markFieldAsChanged(fieldId, numValue, originalValue);
    
    // 로컬 월별 직원 데이터만 업데이트
    setEmployees(prev => 
      prev.map(emp => {
        if (emp.id === id) {
          const deductions = calculateDeductions({...emp, familyCount: numValue});
          return {...emp, familyCount: numValue, tax: deductions.tax, netSalary: deductions.netSalary};
        }
        return emp;
      })
    );
    setIsDataChanged(true);
  };
  
  // 🔒 파트 정규화 — 파트는 개별수당으로만 급여를 지급한다(정책 결정 2026-08-04).
  //    고정 급여 필드를 0으로 정리하고, 퇴직금·세금은 정본 산식으로 재계산한다.
  //    (초과수당은 연장근로시간에서 자동 계산되므로 함께 0)
  const normalizePartEmployee = (emp: Employee): Employee => {
    const cleared = clearPartPayFields(emp);
    const { tax, netSalary } = calculateDeductions(cleared);
    return { ...cleared, tax, netSalary };
  };

  const updateEmployeePosition = (id: number | string, position: string) => {
    // 🔒 파트 전환은 업데이터 밖에서 완결 — 이전 직위의 고정 급여 값(신규 추가 기본 월급 270만 등)이
    //    잠긴 칸에 남아 매달 이월되는 것을 차단한다.
    //    · confirm을 setEmployees 업데이터 안에서 호출하면 StrictMode 이중 호출로 확인창이 두 번 뜬다.
    //    · 인건비가 줄어드는 변경이므로 목표매출도 함께 재계산해야 한다 (updateAllowance와 동일 규칙).
    //      빠뜨리면 저장된 목표가 실제 인건비보다 높게 남아 초과분·인센티브가 과소 계산된다.
    if (position === '파트') {
      const target = employees.find(emp => emp.id === id);
      if (target && target.position !== '파트') {
        const hasResidual = PART_LOCKED_FIELDS.some(field => Number(target[field]) > 0);
        if (hasResidual) {
          const confirmed = window.confirm(
            `'${target.name}'을(를) 파트로 변경합니다.\n\n` +
            `파트는 개별수당으로만 급여를 지급하므로 다음 항목이 0으로 초기화됩니다:\n` +
            `월급(세전)·비과세총액·식대·기숙사·연장근로시간·초과수당·기타·가족수\n\n` +
            `계속하시겠습니까?`
          );
          if (!confirmed) return;
        }

        // 실장이었다면 실장 전용 필드도 함께 제거 (기존 '실장→타 직책' 분기와 동일 규칙)
        const { directorIncentiveRate, teamCostAllocation, ...rest } = target as any;
        const normalized = normalizePartEmployee({ ...rest, position: '파트' } as Employee);
        const updatedEmployees = employees.map(emp => (emp.id === id ? normalized : emp));

        setEmployees(updatedEmployees);
        overtimeAutoCheckRef.current = true; // ⏰ 인원 구성 변경 → 5인 자동 판정
        setIsDataChanged(true);
        if (target.team) {
          recalculateTeamTargetSales(target.team, updatedEmployees);
        }
        return;
      }
    }

    setEmployees(prev => {
      const newEmployees = [...prev];
      const employeeIndex = newEmployees.findIndex(emp => emp.id === id);

      if (employeeIndex === -1) {
        return prev;
      }

      const currentEmployee = newEmployees[employeeIndex];
      
      // 🔧 이미 원하는 상태인 경우 변경하지 않음
      if (currentEmployee.position === position) {
        return prev;
      }

      if (position === '팀장' && currentEmployee.position !== '팀장') {
        
        // 같은 팀에 이미 팀장이 있는지 확인
        const existingLeader = newEmployees.find(emp => 
          emp.team === currentEmployee.team && 
          emp.position === '팀장' && 
          emp.id !== id
        );
        
        if (existingLeader) {
          // 🔧 경고창 중복 방지 로직 추가
          const alertKey = `position_${currentEmployee.team}_${id}_${existingLeader.id}`;
          
          // 이미 처리 중인 경우 중복 실행 방지
          if (alertShownRef.current.has(alertKey)) {
            return prev;
          }
          
          // 처리 시작 표시
          alertShownRef.current.add(alertKey);
          
          // 경고창 표시
          const confirmMessage = `${currentEmployee.team}팀에 이미 팀장 '${existingLeader.name}'이 있습니다.\n\n` +
                               `'${currentEmployee.name}'을(를) 새로운 팀장으로 설정하면 ` +
                               `기존 팀장 '${existingLeader.name}'은(는) 팀원으로 변경됩니다.\n\n` +
                               `계속하시겠습니까?`;
          
          // 경고창 결과에 따른 처리
          const confirmed = window.confirm(confirmMessage);
          // 처리 완료 → 동일 조합 재시도 허용. StrictMode(dev)의 동기 이중 호출 창이 지난 뒤
          // 지연 삭제해 confirm이 1회만 표시되도록 함 (키를 영구 보존하면 취소 후 재시도가 막힘).
          setTimeout(() => { alertShownRef.current.delete(alertKey); }, 0);
          if (confirmed) {
            // 사용자가 확인하면 기존 팀장을 팀원으로 변경하고 새로운 팀장 설정
            addChangeLog(`${currentEmployee.team}팀 팀장 변경: ${existingLeader.name}→팀원, ${currentEmployee.name}→팀장`);
            
            // 🔧 기존 팀장을 팀원으로 변경하고 새로운 팀장 설정을 한 번에 처리
            const updatedEmployees = newEmployees.map(emp => {
              if (emp.team === currentEmployee.team && emp.position === '팀장' && emp.id !== id) {
                // 기존 팀장을 팀원으로 변경
                return {...emp, position: '팀원'};
              }
              if (emp.id === id) {
                // 새로운 직원을 팀장으로 변경
                return {...emp, position: '팀장'};
              }
              return emp;
            });

            overtimeAutoCheckRef.current = true; // ⏰ 인원 구성 변경 → 5인 자동 판정
            return updatedEmployees;
          }
          // ❌ 취소: 아무 변경 없이 종료 — 기존 팀장 유지. 아래 분기(실장 필드 제거/일반 직책 변경)로의
          //    fall-through를 차단해 팀장 중복 생성 및 overtimeAutoCheckRef 오발동 방지 (코드리뷰 BUG1)
          return prev;
        }
      }

      // 실장으로 변경하는 경우: 기존 실장 확인 후 팀을 '실장'으로 설정하고 기본 필드 초기화
      if (position === '실장') {
        const existingDirector = newEmployees.find(emp => emp.position === '실장' && emp.id !== id);
        if (existingDirector) {
          alert(`이미 실장이 존재합니다 (${existingDirector.name}). 실장은 1명만 지정할 수 있습니다.`);
          return prev;
        }
        newEmployees[employeeIndex] = {
          ...currentEmployee,
          position: '실장',
          // team 변경 없음 - 현재 소속 팀 유지
          directorIncentiveRate: currentEmployee.directorIncentiveRate || 10,
          teamCostAllocation: currentEmployee.teamCostAllocation || {},
        };
        overtimeAutoCheckRef.current = true; // ⏰ 인원 구성 변경 → 5인 자동 판정
        return newEmployees;
      }

      // 실장에서 다른 직책으로 변경하는 경우: 실장 필드 제거
      if (currentEmployee.position === '실장' && position !== '실장') {
        const { directorIncentiveRate, teamCostAllocation, ...rest } = currentEmployee;
        const demoted = { ...rest, position } as Employee;
        newEmployees[employeeIndex] = position === '파트' ? normalizePartEmployee(demoted) : demoted;
        overtimeAutoCheckRef.current = true; // ⏰ 인원 구성 변경 → 5인 자동 판정
        return newEmployees;
      }

      // 🔧 경고창이 없는 경우 또는 팀장이 아닌 다른 직책으로 변경하는 경우
      newEmployees[employeeIndex] = position === '파트'
        ? normalizePartEmployee({ ...currentEmployee, position })
        : {...currentEmployee, position};
      overtimeAutoCheckRef.current = true; // ⏰ 인원 구성 변경(파트↔비파트 포함) → 5인 자동 판정
      return newEmployees;
    });
    setIsDataChanged(true);
  };

  // 실장 인센티브 비율 업데이트
  const updateDirectorIncentiveRate = (id: number | string, rate: number) => {
    setEmployees(prev =>
      prev.map(emp => emp.id === id ? { ...emp, directorIncentiveRate: rate } : emp)
    );
    setIsDataChanged(true);
  };

  // 실장 팀별 배분 비율 업데이트
  const updateDirectorTeamAllocation = (id: number | string, teamName: string, rate: number) => {
    setEmployees(prev =>
      prev.map(emp => {
        if (emp.id !== id) return emp;
        const merged = { ...(emp.teamCostAllocation || {}), [teamName]: rate };
        // 현재 존재하는 팀만 유지 (stale 엔트리 제거)
        const validTeams = localTeams.filter(t => t !== '미지정');
        const newAllocation: Record<string, number> = {};
        for (const t of validTeams) {
          if (merged[t] !== undefined) newAllocation[t] = merged[t];
        }
        return { ...emp, teamCostAllocation: newAllocation };
      })
    );
    setIsDataChanged(true);
  };
  
  const updatePersonnelPercentage = (value: string | number) => {
    // 🔒 전역 계산 상태 확인 (일괄 인건비율 업데이트 상호 배제)
    if (isGlobalCalculationRunning) {
      return;
    }

    setIsGlobalCalculationRunning(true);

    try {
      const fieldId = 'personnelPercentage';
      const numValue = Number(value) || 0;
      const originalValue = getOriginalValue(fieldId);

      // 현재 월별 데이터의 인건비율과 비교하여 불필요한 렌더링을 방지 (수정됨)
      const currentMonthlyPercentage = budgetData.monthlyData?.[selectedMonth]?.personnelPercentage || 0;
    
      // 부동소수점 오차를 고려한 비교 (수정됨)
      if (Math.abs(numValue - currentMonthlyPercentage) < 0.001) {
        return; // finally 블록에서 정리됨
      }
    
    debugGroup(`전체 인건비율 변경 감지`, () => {
      debugLog(`인건비율 변경 상황`, {
        '이전 인건비율': currentMonthlyPercentage + '%',
        '새로운 인건비율': numValue + '%',
        '변경량': (numValue - currentMonthlyPercentage).toFixed(1) + '%p',
        '선택된 월': selectedMonth
      });
    });
    
    markFieldAsChanged(fieldId, numValue, originalValue);
    
    setBudgetData(prev => {
      // 🔧 월별 데이터에서 기존 팀별 인건비율 가져오기 (Firebase 경로: budget/currentBudget/monthlyData/[월]/teamPersonnelPercentages/[팀명])
      const existingMonthlyTeamPersonnelPercentages = prev.monthlyData?.[selectedMonth]?.teamPersonnelPercentages || {};
      
      debugLog(`기존 팀별 인건비율 확인`, {
        '월별 팀별 인건비율': existingMonthlyTeamPersonnelPercentages,
        'Firebase 경로': `budget/currentBudget/monthlyData/${selectedMonth}/teamPersonnelPercentages`,
        '새로운 전체 인건비율': numValue + '%'
      });
      
      // 🔧 전체 인건비 총액 계산 (Firebase 데이터 직접 사용)
      const firebaseEmployeesForTotal = prev.monthlyData?.[selectedMonth]?.employees || employees;
      const totalBaseSalaryAndAllowances = calculateSalaryTotal(firebaseEmployeesForTotal);
      
      // 🔧 전체 목표매출 계산 (유틸리티 함수 사용)
      const newTargetSales = calculatePureTargetSales(totalBaseSalaryAndAllowances, numValue);
      const newTeamTargetSales: {[key: string]: number} = {};
      // 🔒 이월금액은 목표와 같은 업데이트에 실린다
      const newTeamCarryover: Record<string, number> = { ...(budgetData.monthlyData?.[selectedMonth]?.teamAdjustmentCarryover || {}) };
      const newTeamPureTargetSales: {[key: string]: number} = {};
      const newTeamPersonnelPercentages: {[key: string]: number} = {};
      
      if (numValue > 0) {
        // 각 팀별 인건비 계산 및 순수 목표매출 업데이트
        if (localTeams && Array.isArray(localTeams)) {
          localTeams.forEach(teamName => {
            if (teamName) {
              debugGroup(`${teamName}팀 전체 인건비율 적용`, () => {
                // 🔧 Firebase 데이터 직접 사용으로 동기화 문제 해결
                const firebaseEmployees = prev.monthlyData?.[selectedMonth]?.employees || employees;
                const teamEmployees = firebaseEmployees.filter(emp => emp.team === teamName);
                
                debugLog(`팀 직원 정보`, {
                  팀명: teamName,
                  직원수: teamEmployees.length,
                  직원명단: teamEmployees.map(emp => `${emp.name}(${emp.position})`).join(', ')
                });
                
                // 팀별 상세 인건비 계산
                debugGroup(`${teamName}팀 인건비 상세 내역`, () => {
                  teamEmployees.forEach(emp => {
                    const individualTotal = emp.salary + (emp.retirement || 0) + (emp.meal || 0) + 
                      (emp.housing || 0) + (emp.overtime || 0) + (emp.individual || 0) + (emp.other || 0);
                    
                    debugLog(`${emp.name} 인건비 상세`, {
                      기본급: emp.salary?.toLocaleString() + '원',
                      퇴직적립금: (emp.retirement || 0).toLocaleString() + '원',
                      식대: (emp.meal || 0).toLocaleString() + '원',
                      주거비: (emp.housing || 0).toLocaleString() + '원',
                      초과수당: (emp.overtime || 0).toLocaleString() + '원',
                      개인수당: (emp.individual || 0).toLocaleString() + '원',
                      기타수당: (emp.other || 0).toLocaleString() + '원',
                      개인총액: individualTotal.toLocaleString() + '원'
                    });
                  });
                });
                
                // 🔒 실장 배분 정본: 실장을 1회만 계상 (인센티브 경로와 동일 정의, 배분누락 방지)
                const teamSalaryTotal = calculateTeamSalaryTotal(firebaseEmployees, teamName, firebaseEmployees);
                const teamPureTargetSales = calculatePureTargetSales(teamSalaryTotal, numValue);
                
                debugCalculationStep(
                  '팀 순수목표매출 계산',
                  {
                    팀인건비총액: teamSalaryTotal.toLocaleString() + '원',
                    새로운인건비율: numValue + '%'
                  },
                  teamPureTargetSales.toLocaleString() + '원',
                  `${teamSalaryTotal.toLocaleString()} ÷ (${numValue}% ÷ 100) = ${teamSalaryTotal.toLocaleString()} ÷ ${numValue/100}`
                );
                
                // 순수 목표매출 업데이트
                newTeamPureTargetSales[teamName] = teamPureTargetSales;
                newTeamPersonnelPercentages[teamName] = numValue;

                // 🔧 중앙집중식 최종 목표매출 계산 (중복 방지 및 일관성 보장)
                const finalTargetResult = calculateFinalTargetSales(
                  teamName,
                  teamPureTargetSales,
                  teamEmployees,
                  'bulk' // 일괄 업데이트 컨텍스트
                );

                const finalTargetSales = finalTargetResult.finalTargetSales;
                const adjustmentApplied = finalTargetResult.adjustmentApplied;
                const previousUnderperformance = finalTargetResult.underperformanceAmount;

                newTeamTargetSales[teamName] = finalTargetSales;
                newTeamCarryover[teamName] = finalTargetResult.underperformanceAmount; // 동결·저장된 이월

                debugLog(`${teamName}팀 전체 인건비율 적용 완료 ✅`, {
                  '💼 팀 인건비 총액': teamSalaryTotal.toLocaleString() + '원',
                  '📊 새로운 인건비율': numValue + '%',
                  '🎯 순수목표매출': teamPureTargetSales.toLocaleString() + '원',
                  '📈 지난달미달성': previousUnderperformance.toLocaleString() + '원',
                  '🏆 최종목표매출': finalTargetSales.toLocaleString() + '원',
                  '🔧 조정적용됨': adjustmentApplied
                });
              });
            }
          });
        }
      }
      
      // 팀 성과 데이터 재계산
      const existingTeamPerformanceData = prev.monthlyData?.[selectedMonth]?.teamPerformanceData || {};
      const newTeamPerformanceData: Record<string, any> = {};
      
      Object.entries(newTeamTargetSales).forEach(([teamName, targetSales]) => {
        if (existingTeamPerformanceData[teamName]) {
          const actualSales = existingTeamPerformanceData[teamName].actualSales || 0;
          newTeamPerformanceData[teamName] = {
            ...existingTeamPerformanceData[teamName],
            targetSales: targetSales,
            exceededSales: Math.max(0, actualSales - targetSales),
            underperformanceSales: Math.max(0, targetSales - actualSales),
            achievementRate: targetSales > 0 ? actualSales / targetSales : 0
          };
          
        }
      });
      
      // 월별 데이터 업데이트 - 완전한 깊은 복사로 다른 월 독립성 보장
      const updatedMonthlyData = JSON.parse(JSON.stringify(prev.monthlyData || {}));
      // 플래그는 이월>0 과 동치 (Codex 재심 P1-1) · 스칼라는 팀 최종목표의 합 (P1-4)
      const bulkFlags: Record<string, boolean> = { ...(prev.monthlyData?.[selectedMonth]?.adjustmentApplied || {}) };
      Object.keys(newTeamCarryover).forEach(t => { bulkFlags[t] = newTeamCarryover[t] > 0; });
      const bulkTotalTarget = Object.values(newTeamTargetSales).reduce((sum: number, v: any) => sum + (Number(v) || 0), 0);
      updatedMonthlyData[selectedMonth] = {
        ...JSON.parse(JSON.stringify(prev.monthlyData?.[selectedMonth] || {})),
        teamTargetSales: newTeamTargetSales,
        teamAdjustmentCarryover: newTeamCarryover, // 🔒 목표와 함께
        adjustmentApplied: bulkFlags,
        teamPureTargetSales: newTeamPureTargetSales,
        targetSales: bulkTotalTarget,
        personnelPercentage: numValue,
        teamPersonnelPercentages: newTeamPersonnelPercentages,
        teamPerformanceData: Object.keys(newTeamPerformanceData).length > 0 
          ? { ...existingTeamPerformanceData, ...newTeamPerformanceData }
          : existingTeamPerformanceData,
        actualSales: prev.monthlyData?.[selectedMonth]?.actualSales || prev.actualSales,
        employees: prev.monthlyData?.[selectedMonth]?.employees || employees
      };
      
      // 🔧 현재월인 경우에만 전역 데이터 동기화 (월별 데이터 독립성 보장)
      const currentYearMonth = getCurrentYearMonth();
      const isCurrentMonth = selectedMonth === currentYearMonth;
      
      const updatedBudgetData = {
        ...prev,
        // 🔧 현재월인 경우에만 전역 데이터를 선택된 월의 데이터로 동기화
        ...(isCurrentMonth ? {
          targetSales: newTargetSales,
          personnelPercentage: numValue,
          teamTargetSales: newTeamTargetSales,
          teamPureTargetSales: newTeamPureTargetSales,
          teamPersonnelPercentages: newTeamPersonnelPercentages,
        } : {}),
        monthlyData: updatedMonthlyData
      };
      
      // 자동 저장 제거 - 수동 저장으로 변경
      // 변경사항만 추적하고 실제 저장은 저장 버튼 클릭 시에만 수행
      
      return updatedBudgetData;
    });
      setIsDataChanged(true);
    } finally {
      // 🔒 전역 계산 상태 해제 (상호 배제 해제)
      setIsGlobalCalculationRunning(false);
    }
  };
  
  // 원본 값 가져오기 헬퍼 함수
  const getOriginalValue = (fieldId: string, employeeId?: number | string): any => {
    if (!originalEmployees.length) return undefined;
    
    if (fieldId.includes('_')) {
      const [fieldType, id] = fieldId.split('_');
      const originalEmployee = originalEmployees.find(emp => emp.id === employeeId || emp.id === Number(id));
      
      if (!originalEmployee) return undefined;
      
      switch (fieldType) {
        case 'salary': return originalEmployee.salary || 0;
        case 'nonTaxable': return originalEmployee.nonTaxable || 0;
        case 'familyCount': return originalEmployee.familyCount || 0;
        case 'position': return originalEmployee.position || '';
        case 'name': return originalEmployee.name || '';
        case 'meal': return originalEmployee.meal || 0;
        case 'housing': return originalEmployee.housing || 0;
        case 'overtimeMinutes': return originalEmployee.overtimeMinutes || 0;
        case 'overtime': return originalEmployee.overtime || 0;
        case 'individual': return originalEmployee.individual || 0;
        case 'other': return originalEmployee.other || 0;
        default: return undefined;
      }
    }
    
    // 전역 필드들
    if (fieldId === 'personnelPercentage') {
      return originalBudgetData?.personnelPercentage || 0;
    }
    if (fieldId === 'targetSales') {
      return originalBudgetData?.targetSales || 0;
    }
    if (fieldId === 'actualSales') {
      return originalBudgetData?.actualSales || 0;
    }
    
    return undefined;
  };

  const updateAllowance = (id: number, field: keyof Employee, value: string) => {
    const fieldId = `${field}_${id}`;
    const numValue = Number(value.replace(/,/g, '')) || 0;
    const originalValue = getOriginalValue(fieldId, id);
    
    markFieldAsChanged(fieldId, numValue, originalValue);
    
    // 🔧 해당 직원의 팀 정보 미리 저장 (재계산을 위해)
    const targetEmployee = employees.find(emp => emp.id === id);
    const targetTeam = targetEmployee?.team;
    
    const updatedEmployees = employees.map(emp => {
      if (emp.id === id) {
        const updatedEmp = { ...emp, [field]: numValue };
        const retirement = resolveRetirementPay(updatedEmp);
        return { ...updatedEmp, retirement };
      }
      return emp;
    });
    
    setEmployees(updatedEmployees);
    setIsDataChanged(true);
    
    // 🚀 인건비 변경 시 해당 팀의 목표매출 즉시 재계산 (실제 변경된 경우에만)
    if (targetTeam && numValue !== originalValue) {
      
      debugLog(`수당 변경으로 인한 목표매출 재계산 요청`, {
        직원ID: id,
        수당필드: field,
        변경값: numValue.toLocaleString() + '원',
        해당팀: targetTeam
      });
      
      // 🚀 수정: 수동 재계산 플래그 간소화 - 연속 수정 지원
      isManualRecalculatingRef.current = true;
      
      // 최신 직원 데이터로 즉시 재계산
      recalculateTeamTargetSales(targetTeam, updatedEmployees);
      
      // 🚀 수정: 100ms 후 즉시 재활성화 (연속 수정 가능)
      setTimeout(() => {
        isManualRecalculatingRef.current = false;
      }, 100);
    }
  };

  // 연장근로시간 업데이트 함수
  const updateOvertimeMinutes = (id: number | string, value: string) => {
    const fieldId = `overtimeMinutes_${id}`;
    const numValue = Number(value) || 0;
    const originalValue = getOriginalValue(fieldId, id);
    
    markFieldAsChanged(fieldId, numValue, originalValue);
    
    // 🔧 해당 직원의 팀 정보 미리 저장 (재계산을 위해)
    const targetEmployee = employees.find(emp => emp.id === id);
    const targetTeam = targetEmployee?.team;
    
    const updatedEmployees = employees.map(emp => {
      if (emp.id === id) {
        // 연장근로시간 업데이트
        const updatedEmp = { ...emp, overtimeMinutes: numValue };

        // 초과수당 자동 계산 (공통 정의 사용 - 토글 재계산과 동일 공식 보장)
        const overtimeHours = numValue / 60; // 분을 시간으로 변환 (디버그 표시용)
        const overtimePay = calculateOvertimePay(emp.salary, numValue, applyOvertimeMultiplier);

        debugCalculationStep(
          '초과수당 자동 계산',
          {
            기본급: emp.salary?.toLocaleString() + '원',
            연장근로시간: numValue + '분 (' + overtimeHours.toFixed(1) + '시간)',
            가산율: applyOvertimeMultiplier ? '150%' : '100%'
          },
          overtimePay.toLocaleString() + '원',
          `${emp.salary?.toLocaleString()} ÷ 209시간 × ${overtimeHours.toFixed(1)}시간 × ${applyOvertimeMultiplier ? 1.5 : 1.0}배`
        );

        // 초과수당은 퇴직금 산식 입력 — 동반 갱신하지 않으면 옛 초과수당 기준 퇴직금이 저장됨
        const retirement = resolveRetirementPay({ ...emp, overtime: overtimePay });
        return { ...updatedEmp, overtime: overtimePay, retirement };
      }
      return emp;
    });

    setEmployees(updatedEmployees);
    setIsDataChanged(true);

    // 🚀 연장근로시간 변경 시 해당 팀의 목표매출 즉시 재계산 (실제 변경된 경우에만)
    if (targetTeam && numValue !== originalValue) {
      
      // 🚀 수정: 수동 재계산 플래그 간소화 - 연속 수정 지원
      isManualRecalculatingRef.current = true;
      
      // 최신 직원 데이터로 즉시 재계산
      recalculateTeamTargetSales(targetTeam, updatedEmployees);
      
      // 🚀 수정: 100ms 후 즉시 재활성화 (연속 수정 가능)
      setTimeout(() => {
        isManualRecalculatingRef.current = false;
      }, 100);
    } else if (targetTeam) {
      // 🔧 값이 동일하더라도 UI 인건비합계가 실제로 변경된 경우 강제 재계산
      
      // 🔧 수동 재계산 시작 - 자동 재계산 방지
      isManualRecalculatingRef.current = true;
      
      // 최신 직원 데이터로 즉시 재계산
      recalculateTeamTargetSales(targetTeam, updatedEmployees);
      
      // 🔧 수동 재계산 완료 - 1초 후 자동 재계산 재활성화
      setTimeout(() => {
        isManualRecalculatingRef.current = false;
      }, 1000);
    }
  };

  const updateEmployeeName = (id: number | string, name: string) => {
    const fieldId = `name_${id}`;
    const originalValue = getOriginalValue(fieldId, id);
    
    markFieldAsChanged(fieldId, name, originalValue);
    
    setEmployees(prev => prev.map(emp => emp.id === id ? { ...emp, name } : emp));
    setIsDataChanged(true);
  };

  const toggleAllChecked = () => {
    const newState = !allChecked;
    setAllChecked(newState);
    
    const newCheckedItems: {[key: string | number]: boolean} = {};
    employees.forEach(emp => {
      newCheckedItems[emp.id] = newState;
    });
    setCheckedItems(newCheckedItems);
  };

  const toggleChecked = (id: number | string) => {
    const newCheckedItems = { ...checkedItems };
    newCheckedItems[id] = !newCheckedItems[id];
    setCheckedItems(newCheckedItems);
    
    // 모든 직원이 체크되었는지 확인
    setAllChecked(employees.every(emp => newCheckedItems[emp.id]));
  };

  useEffect(() => { // employees 배열 변경시 checkedItems 초기화
      const newCheckedItems: {[key: string | number]: boolean} = {};
      employees.forEach(emp => {
        newCheckedItems[emp.id] = false;
      });
      setCheckedItems(newCheckedItems);
      setAllChecked(false);
  }, [employees]);

  // 중복 직원 감지 및 통합 기능이 제거되었습니다.

  const addEmployee = () => {

    // 현재 월별 직원 데이터와 글로벌 직원 데이터를 결합하여 사용 가능한 최대 ID 계산
    const allEmployeeIds = [...employees, ...globalEmployees].map(emp => emp.id);
    const newId = allEmployeeIds.length > 0 ? Math.max(...allEmployeeIds) + 1 : 1;

    // 기본 이름에 중복이 있으면 알파벳 접미사 추가
    const defaultName = '새 직원';
    const uniqueName = generateUniqueName(defaultName, employees);
    
    const defaultNewEmployee: Employee = {
      id: newId, 
      name: uniqueName, // 알파벳 접미사가 붙은 이름으로 저장
      team: localTeams.length > 0 ? localTeams[0] : '미지정', 
      position: '팀원', 
      salary: 2700000, 
      tax: 0, 
      netSalary: 0, 
      incentive: 0,
      familyCount: 0,
      retirement: resolveRetirementPay({ position: '팀원', salary: 2700000 }), // 초기값도 정본 산식 (저장 전 UI 인건비·목표매출 과소 방지)
      meal: 200000,
      housing: 0, 
      overtime: 0,
      overtimeMinutes: 0, 
      individual: 0, 
      other: 0, 
      nonTaxable: 200000 // 기본 비과세(식대) 또는 총 비과세액 입력값으로 사용
    };
    const { tax, netSalary, nonTaxableTotal } = calculateDeductions(defaultNewEmployee);
    const finalNewEmployee: Employee = { ...defaultNewEmployee, tax, netSalary, nonTaxable: nonTaxableTotal };

    // 로컬 월별 직원 데이터만 업데이트
    setEmployees(prev => [...prev, finalNewEmployee]);
    overtimeAutoCheckRef.current = true; // ⏰ 직원 추가 → 5인 자동 판정
    // checkedItems도 업데이트
    setCheckedItems(prev => ({ ...prev, [finalNewEmployee.id]: false }));

    // 즉시 변경사항 플래그 설정
    addChangeLog(`직원 추가: ${uniqueName}`);
  };

  const deleteEmployees = () => {
    // 삭제할 직원 ID 목록을 추출
    const employeeIdsToDelete = employees
      .filter(emp => checkedItems[emp.id])
      .map(emp => emp.id);
    
    if (employeeIdsToDelete.length === 0) {
      alert('삭제할 직원을 선택해주세요.');
      return;
    }
    
    // 삭제 확인
    if (!window.confirm(`선택한 ${employeeIdsToDelete.length}명의 직원을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`)) {
      return;
    }
    
    // ⚠️ 월별 선택 삭제는 전역(직원 마스터)에 전파하지 않음 (월별 독립성 유지)
    //    전역 삭제가 필요하면 deleteEmployeesGlobally(관리자 전용)를 사용할 것

    // 삭제될 직원들의 이름 수집
    const deletedEmployeeNames = employees
      .filter(emp => checkedItems[emp.id])
      .map(emp => emp.name);
    
    // 로컬 월별 직원 목록에서 삭제
    setEmployees(prev => prev.filter(emp => !checkedItems[emp.id]));
    overtimeAutoCheckRef.current = true; // ⏰ 직원 삭제 → 5인 자동 판정
    
    // checkedItems도 업데이트
    const newCheckedItems = { ...checkedItems };
    employees.forEach(emp => {
      if (checkedItems[emp.id]) {
        delete newCheckedItems[emp.id];
      }
    });
    setCheckedItems(newCheckedItems);
    setAllChecked(false);
    
    // 변경사항 로그 추가
    deletedEmployeeNames.forEach(name => {
      addChangeLog(`직원 삭제: ${name}`);
    });
    
    // 삭제 완료 알림
    alert(`${employeeIdsToDelete.length}명의 직원이 삭제되었습니다. 저장을 클릭하면 ${selectedMonth} 월 데이터에서 삭제됩니다. (전역 직원 마스터에는 영향을 주지 않습니다.)`);
  };

  // 🗑️ 전역(직원 마스터) 삭제 - 관리자 전용, 전역에서 완전히 제거하는 유일한 경로
  const deleteEmployeesGlobally = async () => {
    if (user?.role !== 'admin') { alert('전역 삭제는 관리자만 가능합니다.'); return; }
    const checkedEmployees = employees.filter(emp => checkedItems[emp.id]);
    if (checkedEmployees.length === 0) { alert('전역에서 삭제할 직원을 선택해주세요.'); return; }
    const checkedNames = [...new Set(checkedEmployees.map(emp => emp.name))];

    // 🛡️ Firestore 문서 ID로 쓸 수 없는 이름(빈값/'/'/'.'/'..'/__...__)은 이 경로로 삭제 불가(예: 'A/B').
    //    UI에서만 지우고 DB에는 남는 불일치를 막기 위해, 유효하지 않은 이름은 제외하고 안내한다.
    const deletableNames = checkedNames.filter(name => isValidEmployeeDocId(name));
    const invalidNames = checkedNames.filter(name => !isValidEmployeeDocId(name));
    if (invalidNames.length > 0) {
      alert(`다음 직원은 이름에 사용할 수 없는 문자(빈값/'/' 등)가 있어 이 화면에서 전역 삭제할 수 없습니다:\n${invalidNames.join(', ')}\n\nFirestore 콘솔에서 직접 정리해야 합니다.${deletableNames.length > 0 ? '\n\n나머지 유효한 직원만 계속 진행합니다.' : ''}`);
    }
    if (deletableNames.length === 0) return;

    // 🛡️ 동명이인 안전장치: 직원 마스터는 이름을 문서 ID(doc('employees', name))로 사용하므로,
    //    선택하지 않은 동명이인이 있으면 이름 기반 삭제가 그 사람의 마스터 레코드까지 파괴한다.
    //    선택한 이름을 공유하는 '선택되지 않은' 직원이 있으면 삭제를 차단한다.
    const deletableNameSet = new Set(deletableNames);
    const collidingNames = [...new Set(
      employees
        .filter(emp => !checkedItems[emp.id] && deletableNameSet.has(emp.name))
        .map(emp => emp.name)
    )];
    if (collidingNames.length > 0) {
      alert(`동명이인(${collidingNames.join(', ')})이 있어 전역 삭제를 진행할 수 없습니다.\n직원 마스터는 이름으로 식별되므로 선택하지 않은 동명이인의 기록까지 삭제됩니다.\n이름을 구분(예: 식별자 추가)한 뒤 다시 시도해주세요.`);
      return;
    }

    if (!window.confirm(`선택한 ${deletableNames.length}명을 직원 마스터(전역)에서 완전히 삭제합니다.\n- 모든 월/성과 화면에서 사라집니다.\n- 연차 잔여·신청 기록은 별도 컬렉션에 보존됩니다.\n이 작업은 되돌릴 수 없습니다. 계속하시겠습니까?`)) return;
    try {
      const newGlobal = (globalEmployees || []).filter(emp => !deletableNameSet.has(emp.name));
      await saveEmployees(newGlobal, deletableNames);
      setGlobalEmployees(newGlobal);
      clearDeletedEmployeeNames();
      setEmployees(prev => prev.filter(emp => !deletableNameSet.has(emp.name)));
      overtimeAutoCheckRef.current = true; // ⏰ 전역 삭제 → 5인 자동 판정
      setCheckedItems({});
      if (typeof setAllChecked === 'function') setAllChecked(false);
      deletableNames.forEach(name => addChangeLog(`전역 직원 삭제: ${name}`));
      alert(`${deletableNames.length}명을 전역에서 삭제했습니다.`);
    } catch (e) {
      console.error('전역 직원 삭제 실패:', e);
      alert('전역 삭제에 실패했습니다. 콘솔을 확인하세요.');
    }
  };

  // 🔍 데이터 무결성 검증 함수
  const validateDataBeforeSave = (data: any, monthToValidate: string) => {
    try {
      
      const monthlyEmployees = data.monthlyData?.[monthToValidate]?.employees || [];
      
      // 1. 직원 데이터 존재 확인 - 빈 배열도 허용
      if (monthlyEmployees.length === 0) {
        console.warn('⚠️ [저장 검증] 직원 데이터가 비어있음 - 경고만 표시하고 저장 진행');
        // 빈 배열도 저장 가능하도록 에러 throw 제거
        // throw new Error(`${monthToValidate}월 직원 데이터가 비어있어 저장을 중단합니다.`);
      }
      
      // 2. 필수 필드 검사
      const invalidEmployees = monthlyEmployees.filter(emp => !emp.name || emp.name.trim() === '');
      if (invalidEmployees.length > 0) {
        console.warn('⚠️ [저장 검증] 이름이 없는 직원 발견:', invalidEmployees);
        throw new Error('이름이 없는 직원이 있어 저장을 중단합니다.');
      }
      
      // 3. 중복 ID 검사
      const employeeIds = monthlyEmployees.map(emp => emp.id);
      const duplicateIds = employeeIds.filter((id, index) => employeeIds.indexOf(id) !== index);
      if (duplicateIds.length > 0) {
        console.warn('⚠️ [저장 검증] 중복 ID 발견:', duplicateIds);
        throw new Error('중복된 직원 ID가 있어 저장을 중단합니다.');
      }
      
      // 4. 급여 데이터 검사
      const invalidSalaries = monthlyEmployees.filter(emp => typeof emp.salary !== 'number' || emp.salary < 0);
      if (invalidSalaries.length > 0) {
        console.warn('⚠️ [저장 검증] 잘못된 급여 데이터 발견:', invalidSalaries.map(emp => ({name: emp.name, salary: emp.salary})));
        throw new Error('잘못된 급여 데이터가 있어 저장을 중단합니다.');
      }

      // 5. 실장 teamCostAllocation 합계 검증 (합계 = 100%, 현재 팀만 기준)
      const validTeams = localTeams.filter(t => t !== '미지정');
      const directors = monthlyEmployees.filter(emp => emp.position === '실장' && emp.teamCostAllocation);
      for (const dir of directors) {
        const allocation = dir.teamCostAllocation as Record<string, number>;
        const allocationSum = validTeams.reduce((s: number, t: string) => s + (Number(allocation[t]) || 0), 0);
        if (allocationSum > 0 && allocationSum !== 100) {
          throw new Error(`실장(${dir.name})의 팀별 인건비 배분 합계가 ${allocationSum}%입니다. 100%가 되어야 저장할 수 있습니다.`);
        }
      }

      return true;
    } catch (error) {
      console.error('❌ [저장 검증] 데이터 무결성 검사 실패:', error);
      throw error;
    }
  };

  const internalSaveAllData = async () => { // savePayrollData에서 이름 변경
    
    if (isSavingRef.current) {
      return;
    }
    isSavingRef.current = true;
    setIsSaving(true);

    // 🔧 저장 정규화(정본 normalizeEmployeeForSave): 퇴직금 + 과세소득 0원 행의 공제 잔재.
    //    퇴직금·공제 컬럼은 표시 전용이라 수동 입력과 충돌하지 않고, 값이 이미 맞으면
    //    같은 객체를 돌려주므로 아래 '변경분만 setState' 최적화가 그대로 유지된다.
    const empsForSave = employees.map(normalizeEmployeeForSave);
    if (empsForSave.some((e, i) => e !== employees[i])) {
      setEmployees(empsForSave); // 화면 상태도 저장값과 일치
    }

    const currentYearMonth = getCurrentYearMonth();
    const isCurrentMonth = selectedMonth === currentYearMonth;
    
    try {
      // 🔧 현재월 전역 동기화는 UPSERT-ONLY로 월별 저장(saveBudgetMonthFieldsAndGlobal) 성공 이후에 수행 (아래 참조)
      //    - 과거/미래 월 저장은 전역 컬렉션을 건드리지 않아 월별 독립성 유지
      //    - 전역 삭제는 deleteEmployeesGlobally(관리자 전용)로만 처리

      // ✅ 순수 월별 데이터 저장 (전역 upsert는 월별 저장 이후 별도 처리)
      
      // teams 컬렉션도 동기화 (새로운 팀이 추가된 경우)
      const currentTeams = teamsData.map(team => team.name);
      const newTeamsFromEmployees = [...new Set(empsForSave
        .map(emp => emp.team)
        .filter(team => team && team.trim() !== '' && team !== '미지정' && !currentTeams.includes(team))
      )];
      
      if (newTeamsFromEmployees.length > 0) {
        
        const newTeamsData = newTeamsFromEmployees.map(teamName => ({
          id: `team_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          name: teamName,
          targetSales: 0
        }));
        
        const mergedTeams = [...teamsData, ...newTeamsData];
        setTeamsContext(mergedTeams);
        // 🔧 신선한 팀 배열 명시 전달 — 클로저 teams 저장으로 신규 팀 누락·롤백되는 것 방지
        await saveTeamData(mergedTeams);
      }
      
      // 2. 월별 데이터 저장 (현재 월이든 과거/미래 월이든 항상 수행)
      
      // 팀 삭제를 반영하기 위해 팀 관련 데이터 필터링
      const activeTeams = [...localTeams];
      const filteredTeamData = (data: Record<string, any>, teams: string[]) => {
        const filtered: Record<string, any> = {};
        teams.forEach(team => {
          if (data[team] !== undefined) {
            filtered[team] = data[team];
          }
        });
        return filtered;
      };

      // 현재 budgetData의 최신 상태를 반영하여 월별 데이터 구성
      const currentMonthData = getSafeMonthlyData(budgetData, selectedMonth);
      
      // 🎯 순수목표매출·인건비총액 재계산 - 저장 시점에 최신 값으로 업데이트 (둘이 항상 일관된 쌍이 되도록 같은 루프에서 계산)
      // 🎯 인건비합계·순수목표·최종목표를 공용 정본에서 한 번에 파생한다.
      //    (제한 저장 경로와 같은 함수를 쓰지 않으면 두 사본이 갈라져
      //     '새 로스터 + 옛 목표'가 다시 영속된다 — payrollMonthTargets.ts 참조)
      const derivedTargets = deriveMonthTargets({
        teams: activeTeams,
        employees: empsForSave,
        getPersonnelPercentage: getEffectivePersonnelPercentage,
        budgetData,
        month: selectedMonth,
        storedTeamTargetSales: currentMonthData.teamTargetSales || budgetData.teamTargetSales || {},
      });
      const recalculatedTeamSalaryTotals = derivedTargets.teamSalaryTotals;
      const recalculatedTeamPureTargetSales = derivedTargets.teamPureTargetSales;
      const recalculatedTeamTargetSales = derivedTargets.teamTargetSales;
      const recalculatedCarryover = derivedTargets.teamAdjustmentCarryover;

      // 🔧 기존 monthlyData를 완전한 JSON 깊은 복사로 독립성 보장  
      const updatedMonthlyData: Record<string, any> = {};
      if (budgetData.monthlyData) {
        // 현재 선택된 월을 제외한 모든 월 데이터를 완전 깊은 복사
        Object.keys(budgetData.monthlyData).forEach(month => {
          if (month !== selectedMonth) {
            updatedMonthlyData[month] = JSON.parse(JSON.stringify(budgetData.monthlyData[month]));
          }
        });
      }
      
      // 실장의 teamCostAllocation에서 stale 팀 엔트리 제거
      const cleanedEmployees = empsForSave.map(emp => {
        if (emp.position !== '실장' || !emp.teamCostAllocation) return emp;
        const validTeamNames = activeTeams.filter(t => t !== '미지정');
        const cleaned: Record<string, number> = {};
        for (const t of validTeamNames) {
          if (emp.teamCostAllocation[t] !== undefined) cleaned[t] = emp.teamCostAllocation[t];
        }
        return { ...emp, teamCostAllocation: cleaned };
      });

      // 현재 선택된 월 데이터 생성 (완전한 JSON 깊은 복사)
      updatedMonthlyData[selectedMonth] = {
          ...JSON.parse(JSON.stringify(currentMonthData)),
          employees: JSON.parse(JSON.stringify(cleanedEmployees)), // 완전한 깊은 복사 (stale 제거됨)
          teams: [...localTeams], // 현재 사용 중인 팀 목록도 저장
          // 전체 합계 값들 (?? 사용: 0도 유효한 월별 값 — 과거 월에 root(현재월) 값이 새는 것 방지)
          // 전체 목표는 팀별 최종목표의 합 — 팀맵과 스칼라가 어긋나지 않게 같은 파생에서 가져온다
          targetSales: derivedTargets.totalTargetSales,
          actualSales: currentMonthData.actualSales ?? budgetData.actualSales ?? 0,
          personnelPercentage: currentMonthData.personnelPercentage ?? budgetData.personnelPercentage ?? 0,
          // 🔒 팀 관련 필드: 월별 값 우선 (root는 현재월일 때만 동기화되므로, 과거 월 저장 시 root를 쓰면 현재월 값으로 clobber됨)
          teamTargetSales: recalculatedTeamTargetSales, // 🎯 순수목표 + 이월금액 (같은 로스터에서 파생)
          teamAdjustmentCarryover: recalculatedCarryover, // 🎯 목표와 같은 저장에 싣는다
          teamPersonnelPercentages: filteredTeamData(currentMonthData.teamPersonnelPercentages || budgetData.teamPersonnelPercentages || {}, activeTeams),
          teamSalaryTotals: recalculatedTeamSalaryTotals, // 🎯 저장 시점 정본 재계산 (순수목표매출과 일관된 쌍)
          // 🔒 월별화된 인센티브 비율: 월별 값 우선 보존 (전역 동결값으로 덮어쓰면 편집 유실됨), 없으면 전역 폴백
          teamIncentiveRatios: filteredTeamData(currentMonthData.teamIncentiveRatios || budgetData.teamIncentiveRatios || {}, activeTeams),
          teamIncentiveCalculations: filteredTeamData(currentMonthData.teamIncentiveCalculations || budgetData.teamIncentiveCalculations || {}, activeTeams),
          teamPerformanceData: filteredTeamData(currentMonthData.teamPerformanceData || budgetData.teamPerformanceData || {}, activeTeams),
          teamPureTargetSales: recalculatedTeamPureTargetSales, // 🎯 재계산된 순수목표매출 사용
          underperformanceDeductionRate: currentMonthData.underperformanceDeductionRate ?? budgetData.underperformanceDeductionRate ?? 50,
          lastUpdated: new Date().toISOString(),
          selectedMonth: selectedMonth, // 🔧 저장된 월 정보를 명시적으로 추가
          // 🔧 importedFromMonth 관리 개선: 현재 월에 직접 저장할 때는 undefined로 설정
          importedFromMonth: undefined, // 현재 월 고유 데이터로 저장
          // 데이터 소스 정보 추가
          dataSource: isCurrentMonth ? 'Firebase컬렉션동기화' : '월별독립데이터',
          // 연장근로 150% 가산 토글 상태 저장
          applyOvertimeMultiplier: applyOvertimeMultiplier
        };

      // 🔧 root actualSales 동기화 값: 숫자만 채택 — 월 actualSales가 실적 캐시 '객체'(2026-01~05 형태)면
      //    캐시의 totalActualSales 숫자를 채택하고, 그것도 없으면 root 미동기화(기존 값 유지).
      //    객체가 root에 주입되면 급여 요약이 [object Object]/NaN으로 표시됨 (DB 쓰기 가드와 동일 규칙)
      const monthActualSalesRaw: any = updatedMonthlyData[selectedMonth].actualSales;
      const rootActualSalesSync: number | undefined =
        typeof monthActualSalesRaw === 'number'
          ? monthActualSalesRaw
          : (typeof monthActualSalesRaw?.totalActualSales === 'number' ? monthActualSalesRaw.totalActualSales : undefined);

      // BudgetData 업데이트
      const updatedBudgetData = {
        ...budgetData,
        // 현재월인 경우에만 전역 데이터를 선택된 월의 데이터로 동기화
        ...(isCurrentMonth ? {
          targetSales: updatedMonthlyData[selectedMonth].targetSales,
          ...(rootActualSalesSync !== undefined ? { actualSales: rootActualSalesSync } : {}),
          // 🔧 root 동기화는 방금 구성한 월 객체 값 사용 (옛 root 자기복사 방지 — recompute 래퍼 삭제 후 유일한 root 갱신 경로)
          teamTargetSales: updatedMonthlyData[selectedMonth].teamTargetSales,
          personnelPercentage: updatedMonthlyData[selectedMonth].personnelPercentage,
          teamPersonnelPercentages: updatedMonthlyData[selectedMonth].teamPersonnelPercentages,
          // ⚠️ teamIncentiveRatios는 freeze-global: root는 동결 폴백이므로 월별 값을 root로 밀어올리지 않음
          teamIncentiveRatios: filteredTeamData(budgetData.teamIncentiveRatios || {}, activeTeams),
          teamSalaryTotals: recalculatedTeamSalaryTotals, // 🎯 월별 저장값과 일관된 정본 재계산 사용
          // ⚠️ root teamIncentiveCalculations·teamPerformanceData 미러는 in-memory에 적재된 적이 없어
          //    여기서 동기화하면 {}로 소거하게 되므로 비접촉 보존 (root 미러 소비자 없음 확인됨)
          teamPureTargetSales: recalculatedTeamPureTargetSales, // 🎯 재계산된 순수목표매출 사용
          underperformanceDeductionRate: updatedMonthlyData[selectedMonth].underperformanceDeductionRate // 명시저장: 차감비율 전역 동기화(현재월)
        } : {}),
        monthlyData: updatedMonthlyData
      };
      
      // 🔍 저장 전 데이터 무결성 검증
      validateDataBeforeSave(updatedBudgetData, selectedMonth);
      
      // AppContext의 budgetData 업데이트
      // 저장 중임을 명시적으로 표시
      isSavingRef.current = true;
    setIsSaving(true);
      
      // setBudgetData 호출을 지연시켜 다른 useEffect들이 실행되지 않도록 함
      setTimeout(() => {
        setBudgetData(updatedBudgetData as BudgetData);
      }, 0);
      
      // 3. Firebase budget 컬렉션에 저장
      
      // 🔍 팀 관련 데이터 저장 상태 확인
      
              // 🔧 TeamPerformanceService를 통한 실제매출 데이터 동기화
        // 🛡️ 오귀속 의심 시 teamPerformanceData의 DB 영속을 차단하기 위한 게이트 플래그
        //    (refresh·캐시 경로에만 있던 게이트를 최빈 쓰기 경로인 급여 [저장]에도 적용)
        let performanceSuspectMisattribution = false;
        try {
          const { TeamPerformanceService } = await import('../../services/teamPerformanceService.ts');
          const { ActualSalesService } = await import('../../services/actualSalesService.ts');

          const calculatedData = await TeamPerformanceService.calculateTeamPerformance(
            selectedMonth,
            teamsData,
            empsForSave,
            updatedBudgetData
          );

        // 🛡️ 오귀속 의심 게이트 (판정 정본: ActualSalesService.isSuspectMisattribution — refresh 경로와 동일 규칙):
        // 진료의사 매핑 부재 창에서 계산된 '미지정' 귀속 결과는 DB 영속 금지, 화면 표시만 유지.
        // 캐시 경유 결과는 플래그가 없으므로(undefined) 재판정 — 부재를 '의심 없음'으로 오독하면 게이트 우회.
        performanceSuspectMisattribution = calculatedData.actualSalesResult
          ? (calculatedData.actualSalesResult.suspectMisattribution
              ?? ActualSalesService.isSuspectMisattribution(calculatedData.actualSalesResult, updatedBudgetData, selectedMonth))
          : false;
        if (performanceSuspectMisattribution) {
          console.warn(
            `[급여 저장] ${selectedMonth} 진료의사 매핑 부재 상태에서 '미지정' 귀속 매출이 감지되어 ` +
            `teamPerformanceData DB 저장을 건너뜁니다 (화면 표시만 유지). 매핑 복구 후 다시 저장하세요.`
          );
        }

        // 계산된 팀 성과 데이터를 월별 데이터에 저장 (오귀속 의심이어도 화면 표시는 유지 — DB 영속만 아래에서 차단)
        updatedBudgetData.monthlyData[selectedMonth] = {
          ...updatedBudgetData.monthlyData[selectedMonth],
          teamPerformanceData: calculatedData.teamPerformanceData,
          totalActualSales: calculatedData.totalActualSales
        };
        
        // 🔧 TeamPerformanceService 계산 후 즉시 setBudgetData 호출하여 UI에 반영
        setBudgetData(updatedBudgetData as BudgetData);
        
                  // 🔧 teamActualSales 상태도 함께 업데이트하여 즉시 UI 반영
          const updatedTeamActualSales: Record<string, number> = {};
          Object.entries(calculatedData.teamPerformanceData).forEach(([teamName, teamData]) => {
            updatedTeamActualSales[teamName] = teamData.actualSales;
          });
          setTeamActualSales(updatedTeamActualSales);
          
          // 🔧 급여 관리에서 저장 후 정확한 실제매출 표시를 위해 
          // TeamPerformanceService에서 계산된 정확한 값으로 teamActualSales 상태 강제 업데이트
          
          // 🔧 저장 완료 후 강제 리렌더링을 위해 약간의 지연 후 다시 setBudgetData 호출
          setTimeout(() => {
            setBudgetData(prev => ({ ...prev }));
          }, 100);
        
      } catch (teamPerformanceError) {
        console.error('❌ [TeamPerformanceService] 실제매출 데이터 동기화 실패:', teamPerformanceError);
        // 실패해도 저장은 계속 진행
      }
      
      // 💾 의존성 변경 예측 - 저장 전
      console.group('💾 [저장 전 의존성 예측]');
      console.groupEnd();

      // 🔒 per-month '소유 필드 한정' 저장 — 급여 저장은 자신이 관리하는 필드만 쓴다.
      //    (월 통째 교체는 doctorTeamMapping·actualSales 캐시 등 타 파이프라인 필드를
      //     인메모리 stale 상태로 파괴했음 — 2026-06 의사매핑 소실 사고의 재발 방지)
      //    화이트리스트는 파일 상단 상수 정본을 사용 (saveSpecificMonthData와 드리프트 방지).
      const savedMonthObj = updatedBudgetData.monthlyData[selectedMonth];
      const monthMergePatch: any = {};
      PAYROLL_OWNED_MONTH_MERGE_FIELDS.forEach(field => {
        // applyPreviousMonthAdjustment·adjustmentApplied 등 값이 없으면(undefined)
        // merge 저장에서 스킵되어 DB 값이 보존됨 — 의도된 동작
        monthMergePatch[field] = savedMonthObj[field];
      });
      const monthReplaceFields: Record<string, any> = {
        importedFromMonth: undefined // 현재 월 고유 데이터로 저장 → 필드 삭제
      };
      PAYROLL_OWNED_MONTH_TEAM_MAP_FIELDS.forEach(field => {
        // 팀맵은 통째 교체(비활성 팀 키 제거) — 급여가 소유한 맵만
        // 🛡️ 오귀속 의심 시 teamPerformanceData는 DB 비접촉: 키 자체를 제외한다
        //    (undefined 대입은 replace에서 deleteField가 되어 기존 값을 지움 — 제외만이 보존).
        //    매핑 복구 후 다시 저장하면 정상 재계산 값이 영속된다.
        if (field === 'teamPerformanceData' && performanceSuspectMisattribution) return;
        monthReplaceFields[field] = savedMonthObj[field];
      });
      if (isCurrentMonth) {
        // 현재월: 월 저장 + 전역 동기화를 '단일 배치'로 커밋
        await saveBudgetMonthFieldsAndGlobal(selectedMonth, monthMergePatch, monthReplaceFields, {
          targetSales: updatedBudgetData.targetSales,
          // actualSales는 숫자일 때만 root 동기화 (실제매출 캐시 '객체'가 root로 새는 오염 차단)
          ...(typeof updatedBudgetData.actualSales === 'number' ? { actualSales: updatedBudgetData.actualSales } : {}),
          personnelPercentage: updatedBudgetData.personnelPercentage,
          teamTargetSales: updatedBudgetData.teamTargetSales,
          teamPersonnelPercentages: updatedBudgetData.teamPersonnelPercentages,
          teamIncentiveRatios: updatedBudgetData.teamIncentiveRatios,
          teamSalaryTotals: updatedBudgetData.teamSalaryTotals,
          // ⚠️ root teamIncentiveCalculations·teamPerformanceData 미러는 in-memory에 적재된 적이 없어
          //    동기화 시 {}로 dot-path 소거가 되므로 전역 동기화·replace 목록에서 제외 (소비자 없음 확인됨)
          teamPureTargetSales: updatedBudgetData.teamPureTargetSales,
          underperformanceDeductionRate: updatedBudgetData.underperformanceDeductionRate // 명시저장: 차감비율 전역 동기화(현재월)
        }, GLOBAL_TEAM_MAP_FIELDS.filter(f => f !== 'teamIncentiveCalculations' && f !== 'teamPerformanceData'));
      } else {
        await saveBudgetMonthFieldsAndGlobal(selectedMonth, monthMergePatch, monthReplaceFields);
      }

      // 🔄 현재월 전역 직원 마스터 UPSERT (삭제는 하지 않음 - deleteEmployeesGlobally에서 처리)
      if (isCurrentMonth) {
        try {
          await saveEmployees([...empsForSave], []); // upsert-only; deletions handled separately
          // ⚠️ 인메모리 전역 마스터를 현재월 로스터로 통째로 덮어쓰지 않는다.
          //    Firestore 쓰기는 upsert-only(삭제 없음)이므로, 현재월에 없는 전역 직원을
          //    메모리에서 지우면 팀 성과·실제매출 등 전역 소비처와 desync가 발생한다.
          //    → 이름 기준으로 현재월 직원을 병합(UPSERT)하여 나머지 전역 직원은 보존.
          const baseGlobal = Array.isArray(globalEmployees) ? globalEmployees : [];
          const currentByName = new Map(empsForSave.map(emp => [emp.name, emp]));
          const mergedGlobal = baseGlobal.map(emp =>
            currentByName.has(emp.name) ? (currentByName.get(emp.name) as Employee) : emp
          );
          const existingNames = new Set(baseGlobal.map(emp => emp.name));
          empsForSave.forEach(emp => {
            if (!existingNames.has(emp.name)) mergedGlobal.push(emp);
          });
          setGlobalEmployees(mergedGlobal);
        } catch (globalSyncError) {
          console.error('전역 직원 동기화 실패(급여 저장은 완료됨):', globalSyncError);
        }
      }

      // 💾 의존성 변경 확인 - 저장 후
      console.group('💾 [저장 후 의존성 확인]');
      console.groupEnd();
      
      // 🔍 저장 후 즉시 검증
      
      // 잠시 대기 후 Firebase에서 다시 읽기 (저장이 완료될 시간을 줌)
      setTimeout(async () => {
        try {
          const savedData = await getBudgetData();
          
          if (!savedData || !savedData.monthlyData) {
            console.error('❌ [저장 검증 실패] Firebase에서 데이터를 읽을 수 없음');
            return;
          }
          
          // 모든 월의 데이터 확인
          Object.keys(savedData.monthlyData || {}).forEach(month => {
            const monthEmployees = savedData.monthlyData[month]?.employees || [];
          });
          
          const savedEmployees = savedData.monthlyData[selectedMonth]?.employees || [];
          
          if (savedEmployees.length !== employees.length) {
            console.error('❌ [저장 검증 실패] 저장된 직원 수가 다름:', {
              요청월: selectedMonth,
              원본: employees.length,
              저장됨: savedEmployees.length,
              차이: Math.abs(employees.length - savedEmployees.length),
              가능한원인: 'Firebase 저장 경로 문제 또는 다른 월에 저장됨'
            });
            
            // 다른 월에 저장되었는지 확인
            Object.keys(savedData.monthlyData || {}).forEach(month => {
              const monthEmployees = savedData.monthlyData[month]?.employees || [];
              if (monthEmployees.length === employees.length && month !== selectedMonth) {
                console.warn(`⚠️ [저장 검증] ${month}월에 ${employees.length}명의 직원이 발견됨 - 잘못된 월에 저장된 것 같음!`);
              }
            });
          } else {
            // 직원 이름 일치 확인
            const originalNames = employees.map(emp => emp.name).sort();
            const savedNames = savedEmployees.map(emp => emp.name).sort();
            const namesMismatch = originalNames.some((name, index) => name !== savedNames[index]);
            
            if (namesMismatch) {
              console.error('❌ [저장 검증 실패] 직원 이름이 다름:', {
                원본: originalNames,
                저장됨: savedNames
              });
            } else {
            }
          }
        } catch (delayedVerificationError) {
          console.error('❌ [저장 검증] 지연된 Firebase 재확인 실패:', delayedVerificationError);
        }
      }, 1000); // 1초 지연으로 Firebase 저장 완료 대기
      
      // 저장 시간 업데이트
      setLastSaveTime(getFormattedTime());
      
      // 📌 모든 필드를 저장 완료 상태로 업데이트
      const allFieldIds = Object.keys(fieldChangeStatus);
      allFieldIds.forEach(fieldId => {
        if (fieldChangeStatus[fieldId]) {
          markFieldAsSaved(fieldId);
        }
      });
      
      // 성공적으로 저장 후 현재 상태를 새로운 원본으로 설정
      setOriginalEmployees(JSON.parse(JSON.stringify(empsForSave)));
      setOriginalTeams(JSON.parse(JSON.stringify(localTeams)));
      setOriginalBudgetData(JSON.parse(JSON.stringify(updatedBudgetData)));
      
      // 변경사항 로그 초기화
      setChangeLog([]);
      
      // 🔧 저장 후 월 이동 시 실행되는 로직들을 실행
      
      // 1. 실제매출 데이터 재로드
      // try {
      //   setIsLoadingActualSales(true);
        
      //   // Firebase에서 실제 월별 수납 및 지출 데이터 가져오기
      //   const [monthlyReceiptData, monthlyExpenseData] = await Promise.all([
      //     getMonthlyReceiptDataByTeam(selectedMonth),
      //     getMonthlyExpenseDataByTeam(selectedMonth)
      //   ]);
        
      //   // 어시스트별 월 수납액 및 지출액 계산
      //   const assistReceiptCache: Record<string, number> = {};
      //   const assistExpenseCache: Record<string, number> = {};
        
      //   // 수납 데이터 처리
      //   if (monthlyReceiptData && typeof monthlyReceiptData === 'object') {
      //     Object.entries(monthlyReceiptData).forEach(([teamName, receipts]) => {
      //       if (Array.isArray(receipts)) {
      //         receipts.forEach((item: any) => {
      //           if (item && typeof item === 'object') {
      //             const assistName = item.assistName || '미지정';
      //             const amount = Number(item.amount) || 0;
      //             assistReceiptCache[assistName] = (assistReceiptCache[assistName] || 0) + amount;
      //           }
      //         });
      //       }
      //     });
      //   }
        
      //   // 지출 데이터 처리
      //   if (monthlyExpenseData && typeof monthlyExpenseData === 'object') {
      //     Object.entries(monthlyExpenseData).forEach(([teamName, expenses]) => {
      //       if (Array.isArray(expenses)) {
      //         expenses.forEach((item: any) => {
      //           if (item && typeof item === 'object') {
      //             const assistName = item.assistName || '미지정';
      //             const amount = Number(item.amount) || 0;
      //             assistExpenseCache[assistName] = (assistExpenseCache[assistName] || 0) + amount;
      //           }
      //         });
      //       }
      //     });
      //   }
        
      //   // 팀별 실제 매출 계산
      //   const newTeamActualSales: Record<string, number> = {};
      //   let newTotalActual = 0;
        
      //   // 모든 어시스트 이름 수집
      //   const allAssistNames = new Set<string>();
      //   Object.keys(assistReceiptCache).forEach(name => allAssistNames.add(name));
      //   Object.keys(assistExpenseCache).forEach(name => allAssistNames.add(name));
        
      //   // 어시스트별로 팀 매핑 및 집계
      //   Array.from(allAssistNames).forEach(assistName => {
      //     if (!assistName || assistName.trim() === '') return;
          
      //     // 어시스트를 팀으로 매핑 (employees에서 찾기)
      //     let teamName = '미지정';
      //     const matchedEmployee = employees.find(emp => 
      //       emp && emp.name && 
      //       emp.name.toLowerCase().trim() === assistName.toLowerCase().trim()
      //     );
          
      //     if (matchedEmployee && matchedEmployee.team) {
      //       teamName = matchedEmployee.team;
      //     }
          
      //     if (!newTeamActualSales[teamName]) {
      //       newTeamActualSales[teamName] = 0;
      //     }
          
      //     // 월별 수납액 및 지출액을 팀별로 합산하여 순수익 계산
      //     const assistReceipt = assistReceiptCache[assistName] || 0;
      //     const assistExpense = assistExpenseCache[assistName] || 0;
      //     const assistProfit = assistReceipt - assistExpense;
          
      //     newTeamActualSales[teamName] += assistProfit;
      //   });
        
      //   // 전체 실제 매출 계산
      //   newTotalActual = Object.values(newTeamActualSales).reduce((sum, amount) => sum + (Number(amount) || 0), 0);
        
      //   // 상태 업데이트
      //   setTeamActualSales(newTeamActualSales);
        
      //   // budgetData의 actualSales도 업데이트
      //   setBudgetData(prev => ({
      //     ...prev,
      //     actualSales: newTotalActual
      //   }));
        
      // } catch (actualSalesError) {
      //   console.error('🔄 [저장 후 새로고침] 실제매출 재로드 실패:', actualSalesError);
      // } finally {
      //   setIsLoadingActualSales(false);
      // }
      
      // 2. 변경사항 플래그 초기화
      setIsDataChanged(false);
      
      // 3. 화면 전환 시 비교 모드 초기화
      setShowComparisonWithPreviousMonth(false);
      setPreviousMonthEmployees([]);
      
      // 원본 데이터가 업데이트되면 자동으로 useEffect에 의해 isDataChanged가 false로 설정됨
      
      // 저장 후 실제로 데이터가 저장되었는지 확인
      setTimeout(() => {
      }, 1000);
      
      // 🔄 저장 완료 후 자동으로 새로고침 실행
      // try {
      //   setIsLoadingActualSales(true);
      //   const result = await TeamPerformanceService.calculateTeamPerformance(selectedMonth, teamsData, employees, budgetData);
      //   await TeamPerformanceService.saveTeamPerformanceData(selectedMonth, result.teamPerformanceData, result.totalActualSales, result.teamTargetSales, budgetData, setBudgetData);
      // } catch (refreshError) {
      //   console.error('❌ [저장 후 자동 새로고침] 새로고침 실패:', refreshError);
      //   // 새로고침 실패해도 저장은 성공했으므로 계속 진행
      // } finally {
      //   setIsLoadingActualSales(false);
      // }

      alert(`${selectedMonth} 급여 데이터가 성공적으로 저장되었습니다.${isCurrentMonth ? ' (현재월: Firebase 컬렉션 + 월별 데이터 모두 업데이트)' : ' (과거/미래월: 월별 데이터만 저장, Firebase 컬렉션은 현재월만 반영)'}`);
    } catch (error) {
      console.error("데이터 저장 실패:", error);
      alert(`데이터 저장 중 오류가 발생했습니다: ${error.message || error}`);
    } finally {
      // 🔧 저장 플래그를 충분한 지연 후에 false로 설정하여 안전성 확보
      setTimeout(() => {
        isSavingRef.current = false;
        setIsSaving(false);
        isAddingEmployeeRef.current = false; // 직원 추가 플래그도 초기화
        
        // 🔧 저장 완료 후 데이터 변경 플래그 해제
        setIsDataChanged(false);
        
        // 🔧 필드 변경 상태도 초기화
        Object.keys(fieldChangeStatus).forEach(fieldId => {
          if (fieldChangeStatus[fieldId]) {
            markFieldAsSaved(fieldId);
          }
        });
        
      }, 1000); // 1초 지연으로 안전성 확보
    }
  };
  
  const getFormattedTime = () => {
    const now = new Date();
    return `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}`;
  };

  // 🚨 월 변경 시 데이터 저장 관리
  // 특정 월의 데이터를 저장하는 함수
  /**
   * 🔒 제한 권한(edit_payroll_limited) 저장 — 실장용.
   *
   * 전체 저장(internalSaveAllData)은 화면의 전 직원 데이터와 팀맵을 통째로 덮어쓴다.
   * 그 경로를 실장에게 열면, 원장이 다른 탭에서 월급을 고치는 중에 실장이 저장할 때
   * 실장 화면에 남아 있던 옛 월급이 전체를 덮어쓴다(실장은 월급 칸을 건드리지도 않았는데).
   *
   * 그래서 이 경로는:
   *  1. 저장 직전 DB를 다시 읽어 그것을 기준(prev)으로 삼고
   *  2. 화면 값 중 연장근로시간·개별수당과 그 파생값만 얹고(buildLimitedPayrollPatch)
   *  3. 그 로스터에서 인건비합계·목표매출을 함께 파생해(deriveMonthTargets) 정합을 유지한다.
   * 나머지 필드·타 파이프라인 필드는 건드리지 않는다.
   */
  const saveLimitedPayrollData = async (): Promise<boolean> => {
    if (isSavingRef.current) return false;
    isSavingRef.current = true;
    setIsSaving(true);
    try {
      // 1) DB 최신값을 기준으로 삼는다 (다중 탭에서 원장이 방금 고친 값을 덮지 않기 위해)
      const freshBudget = await getBudgetData();
      const freshMonth = freshBudget?.monthlyData?.[selectedMonth];
      const prevEmployees = Array.isArray(freshMonth?.employees) ? freshMonth.employees : null;
      if (!prevEmployees) {
        alert(`${selectedMonth} 급여 데이터가 아직 저장되지 않았습니다.\n관리자가 먼저 저장한 뒤에 입력해 주세요.`);
        return false;
      }

      // 2) 허용 필드와 그 파생값만 얹는다
      // 파생값(초과수당·퇴직금·세금·실지급액)은 DB 월급 + 실장 입력이 합쳐진 행에서 다시 계산한다.
      // 화면 값을 그대로 베끼면, 원장이 다른 탭에서 월급을 올린 경우
      // '새 월급 + 옛 월급 기준 세금'이 한 행에 섞여 저장된다.
      const { employees: patchedEmployees, changedIds } = buildLimitedPayrollPatch(
        prevEmployees,
        employees,
        (merged: any) => {
          const overtime = calculateOvertimePay(
            merged.salary || 0,
            merged.overtimeMinutes || 0,
            applyOvertimeMultiplier
          );
          const retirement = resolveRetirementPay({ ...merged, overtime });
          const { tax, netSalary } = calculateDeductions({ ...merged, overtime, retirement });
          return { overtime, retirement, tax, netSalary };
        }
      );

      if (changedIds.length === 0) {
        alert('변경된 연장근로시간·개별수당이 없습니다.');
        return false;
      }

      // 3) 같은 로스터에서 인건비합계·목표매출을 함께 파생 (전체 저장과 동일 정본)
      const freshTeams: string[] = Array.isArray(freshMonth?.teams) && freshMonth.teams.length > 0
        ? freshMonth.teams
        : localTeams;
      const derived = deriveMonthTargets({
        teams: freshTeams,
        employees: patchedEmployees,
        getPersonnelPercentage: (teamName: string) =>
          Number(freshMonth?.teamPersonnelPercentages?.[teamName]) ||
          Number(freshBudget?.teamPersonnelPercentages?.[teamName]) ||
          Number(freshMonth?.personnelPercentage) ||
          Number(freshBudget?.personnelPercentage) || 0,
        budgetData: freshBudget,
        month: selectedMonth,
        storedTeamTargetSales: freshMonth?.teamTargetSales || {},
      });

      await saveBudgetMonthReplace(
        selectedMonth,
        {
          employees: JSON.parse(JSON.stringify(patchedEmployees)),
          targetSales: derived.totalTargetSales,
          lastUpdated: new Date().toISOString(),
        },
        {
          teamSalaryTotals: derived.teamSalaryTotals,
          teamPureTargetSales: derived.teamPureTargetSales,
          teamTargetSales: derived.teamTargetSales,
          teamAdjustmentCarryover: derived.teamAdjustmentCarryover,
        }
      );

      // 화면 상태도 저장본과 일치시킨다 (전체 저장과 달리 로스터를 DB 기준으로 되돌린다)
      setEmployees(patchedEmployees as any);

      // 🔧 '변경됨' 상태 정리 — 전체 저장과 같은 순서로.
      //    isDataChanged만 false로 두면 소용없다: 칸별 변경 목록(fieldChangeStatus)에 변경이 남아 있으면
      //    동기화 이펙트가 즉시 isDataChanged를 다시 true로 켠다. 그래서 저장 후에도
      //    "연장근로시간·개별수당 저장* / 변경사항이 있습니다"가 계속 떠 있었다(원장 보고).
      Object.keys(fieldChangeStatus).forEach(fieldId => {
        if (fieldChangeStatus[fieldId]) markFieldAsSaved(fieldId);
      });
      setOriginalEmployees(JSON.parse(JSON.stringify(patchedEmployees)));
      setChangeLog([]);
      setLastSaveTime(getFormattedTime());
      setIsDataChanged(false);

      alert(`${selectedMonth} 연장근로시간·개별수당을 저장했습니다. (${changedIds.length}명)`);
      return true;
    } catch (error: any) {
      console.error('[급여관리] 제한 권한 저장 실패:', error);
      alert(`저장 중 오류가 발생했습니다: ${error?.message || error}`);
      return false;
    } finally {
      // 전체 저장과 동일: setEmployees 뒤에 도는 100ms 자동 재계산 타이머가 dirty를 다시 켤 수 있으므로,
      // 그보다 늦게 한 번 더 정리한다.
      setTimeout(() => {
        isSavingRef.current = false;
        setIsSaving(false);
        setIsDataChanged(false);
        Object.keys(fieldChangeStatus).forEach(fieldId => {
          if (fieldChangeStatus[fieldId]) markFieldAsSaved(fieldId);
        });
      }, 1000);
    }
  };

  const saveSpecificMonthData = async (targetMonth: string, monthEmployees: Employee[], monthBudgetData: any) => {

    try {
      // 🔒 해당 월의 기존(월별) 값 - 월별 스코핑 상수 유실 방지용 (monthBudgetData는 root 복사본이라 상수는 동결/stale)
      const existingMonth = monthBudgetData.monthlyData?.[targetMonth] || {};
      // 🔒 월별 값 우선 + ??(0도 유효): monthBudgetData는 root 복사본이라 과거 월엔 현재월 값이 새는 clobber가 됨.
      //    편집 중 재계산 경로가 monthlyData[targetMonth]에 이미 올바른 값을 써두므로 그 월의 값을 그대로 보존한다.
      // 🔒 '소유 필드 한정' 저장 — internalSaveAllData와 동일한 파일 상단 화이트리스트 상수 정본 사용(드리프트 방지).
      //    doctorTeamMapping·actualSales 캐시·totalActualSales 등 타 파이프라인 필드는 절대 건드리지 않는다.
      // 🔧 저장 정규화(internalSaveAllData와 같은 정본): 퇴직금 + 과세소득 0원 행의 공제 잔재
      const normalizedMonthEmployees = monthEmployees.map(normalizeEmployeeForSave);
      // 🔧 정규화 대칭 완성: 인건비총액·순수목표매출도 '정규화된 로스터' 기준으로 정본 재계산해 영속 —
      //    퇴직금만 재계산하고 totals는 정규화 전 스냅샷을 저장하면 '정규화 로스터 + 정규화 전 totals'
      //    불일치 쌍이 영속됨(인건비율 나눗셈으로 편차 증폭). 인건비율은 아래에 저장되는 월별 값과
      //    동일한 소스를 사용해 (totals, 순수목표매출, 인건비율)이 항상 일관된 쌍이 되도록 한다.
      const monthTeamPersonnelPercentages =
        existingMonth.teamPersonnelPercentages || monthBudgetData.teamPersonnelPercentages || {};
      // 🎯 인건비합계·순수목표·최종목표를 공용 정본에서 파생 (internalSaveAllData와 같은 함수 —
      //    두 저장 경로가 사본을 각자 들고 있으면 드리프트로 '새 로스터 + 옛 목표'가 재발한다)
      const monthDerived = deriveMonthTargets({
        teams: localTeams,
        employees: normalizedMonthEmployees,
        // 🔒 인건비율 폴백 체인 4단 (월별팀→전역팀→월별전체→전역전체) — 이 경로는 월별 스냅샷을 쓴다
        getPersonnelPercentage: (teamName: string) =>
          Number(existingMonth.teamPersonnelPercentages?.[teamName]) ||
          Number(monthBudgetData.teamPersonnelPercentages?.[teamName]) ||
          Number(existingMonth.personnelPercentage) ||
          Number(monthBudgetData.personnelPercentage) || 0,
        budgetData: monthBudgetData,
        month: targetMonth,
        storedTeamTargetSales: existingMonth.teamTargetSales || monthBudgetData.teamTargetSales || {},
      });
      const recalculatedTeamSalaryTotals = monthDerived.teamSalaryTotals;
      const recalculatedTeamPureTargetSales = monthDerived.teamPureTargetSales;
      const recalculatedTeamTargetSales = monthDerived.teamTargetSales;
      const recalculatedCarryover = monthDerived.teamAdjustmentCarryover;

      const ownedMonthValues: Record<string, any> = {
        employees: JSON.parse(JSON.stringify(normalizedMonthEmployees)),
        lastUpdated: new Date().toISOString(),
        teams: localTeams,
        selectedMonth: targetMonth,
        // dataSource는 미기록 (undefined → merge 스킵으로 DB 보존 — 월 전환 저장은 소스 판정을 바꾸지 않음)
        // 전체 목표 = 팀별 최종목표의 합 — 기존 스칼라를 그대로 두면 팀맵과 어긋난다 (Codex 재심 P1-4)
        targetSales: monthDerived.totalTargetSales,
        personnelPercentage: existingMonth.personnelPercentage ?? monthBudgetData.personnelPercentage ?? 0,
        underperformanceDeductionRate: existingMonth.underperformanceDeductionRate ?? monthBudgetData.underperformanceDeductionRate ?? 50,
        // 연장근로 150% 토글은 UI 상태가 정본 (internalSaveAllData와 동일 규칙 — 편집이 monthlyData에 즉시 반영되지 않음)
        applyOvertimeMultiplier: applyOvertimeMultiplier,
        // 조정 플래그 2종은 편집 시 monthlyData에 즉시 기록되므로 월별 값 그대로 (undefined면 merge 스킵 → DB 보존)
        applyPreviousMonthAdjustment: existingMonth.applyPreviousMonthAdjustment,
        adjustmentApplied: existingMonth.adjustmentApplied,
        teamTargetSales: recalculatedTeamTargetSales, // 🎯 순수목표 + 이월금액 (정규화 로스터 기준)
        teamAdjustmentCarryover: recalculatedCarryover, // 🎯 목표와 같은 저장에 싣는다
        teamPersonnelPercentages: monthTeamPersonnelPercentages,
        teamSalaryTotals: recalculatedTeamSalaryTotals, // 🎯 정규화 로스터 기준 정본 재계산 (순수목표매출과 일관된 쌍)
        teamIncentiveRatios: existingMonth.teamIncentiveRatios || monthBudgetData.teamIncentiveRatios || {},
        teamPureTargetSales: recalculatedTeamPureTargetSales, // 🎯 정규화 로스터 기준 정본 재계산
        // 존재값 보존: 미적재(undefined)면 아래 replace 구성에서 키 제외 → DB 비접촉 ({} 소거 방지)
        teamIncentiveCalculations: existingMonth.teamIncentiveCalculations
        // ⚠️ teamPerformanceData는 여기 미포함 — 급여는 '재계산 시에만 기록'(internalSaveAllData 게이트 경유).
        //    재계산 없는 in-memory 스냅숏 replace는 재로드 실패 시 {} 소거(다음 달 미달성 조정 0 오판)·
        //    다중 세션 stale 역행을 일으키므로 이 경로에서는 비접촉.
      };
      const mergePatch: any = {};
      PAYROLL_OWNED_MONTH_MERGE_FIELDS.forEach(field => {
        mergePatch[field] = ownedMonthValues[field];
      });
      // 팀맵은 dot-path 통째 교체(replace) — merge는 빠진 키를 못 지워 삭제된 팀의 유령 키가 남음
      const replaceFields: Record<string, any> = {};
      PAYROLL_OWNED_MONTH_TEAM_MAP_FIELDS.forEach(field => {
        // 🔒 teamPerformanceData 비접촉: 이 경로는 재계산 없이 스냅숏만 보유 — replace 시 {} 소거·
        //    stale 역행 창이 생기므로 유령 키 정리는 재계산하는 internalSaveAllData에 위임한다.
        if (field === 'teamPerformanceData') return;
        // undefined는 replace에서 deleteField(필드 삭제)가 되므로 키 자체를 제외해 DB 값 보존
        if (ownedMonthValues[field] === undefined) return;
        replaceFields[field] = ownedMonthValues[field];
      });

      await saveBudgetMonthReplace(targetMonth, mergePatch, replaceFields);
      return true;
    } catch (error) {
      console.error(`[급여관리] ${targetMonth}월 데이터 저장 실패:`, error);
      return false;
    }
  };
  
  // 월 변경 전 데이터 백업 및 저장 확인
  useEffect(() => {
    // 첫 로드시는 스킵
    if (!lastLoadedMonthRef.current) return;
    
    // 월이 실제로 변경되었을 때만
    if (lastLoadedMonthRef.current !== selectedMonth) {
      // 변경사항이 있으면 저장 확인
      if (isDataChanged) {
        const previousMonth = lastLoadedMonthRef.current;
        // 현재 상태를 백업 (월 변경 전 데이터)
        const backupEmployees = JSON.parse(JSON.stringify(employees));
        const backupBudgetData = JSON.parse(JSON.stringify(budgetData));
        
        // 저장 확인
        const shouldSave = window.confirm(
          `💾 ${previousMonth}월의 변경사항이 저장되지 않았습니다.\n\n저장하지 않고 ${selectedMonth}월로 이동하면 변경사항이 손실됩니다.\n\n저장하시겠습니까?`
        );
        
        if (shouldSave) {
          // 백업된 데이터로 이전 월 저장
          saveSpecificMonthData(
            previousMonth,
            backupEmployees,
            backupBudgetData
          ).then((success) => {
            if (success) {
              // 저장 완료 후 변경사항 플래그 초기화
              setIsDataChanged(false);
            } else {
              alert(`${previousMonth}월 데이터 저장에 실패했습니다.`);
            }
          });
        } else {
          // 저장하지 않고 이동하는 경우 변경사항 플래그 초기화
          setIsDataChanged(false);
        }
      }
    }
  }, [selectedMonth]); // selectedMonth가 변경될 때마다 실행
  
  // 현재 연월 가져오기 (YYYY-MM 형식)
  const getCurrentYearMonth = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };

  // 🔧 useMemo로 월별 팀 목표매출 데이터 메모이제이션
  const monthlyTeamTargetSales = useMemo(() => {
    return budgetData.monthlyData?.[selectedMonth]?.teamTargetSales || {};
  // 🔧 FIX C10-D2: JSON.stringify 제거 - budgetData.monthlyData 참조로 변경 감지
  }, [budgetData.monthlyData, selectedMonth]);

  // 🔧 useMemo로 전체 목표매출 계산 메모이제이션
  const totalTargetSales = useMemo(() => {
    return Object.values(monthlyTeamTargetSales).reduce((sum, val) => sum + (Number(val) || 0), 0);
  }, [monthlyTeamTargetSales]);

  // 월별 목표매출 계산 로그

  // 🔧 팀별 통계 메모이제이션
  const teamStatsData = useMemo(() => {
    const teamStats: Record<string, { 팀장수: number; 팀원수: number; 총인원: number }> = {};
    employees.forEach(emp => {
      if (!teamStats[emp.team]) {
        teamStats[emp.team] = { 팀장수: 0, 팀원수: 0, 총인원: 0 };
      }
      teamStats[emp.team].총인원++;
      if (emp.position === '팀장') {
        teamStats[emp.team].팀장수++;
      } else {
        teamStats[emp.team].팀원수++;
      }
    });
    return teamStats;
  // 🔧 FIX C10-D3: JSON.stringify + employees.length 제거 - employees 참조로 충분
  }, [employees]);

  // 🔧 FIX C10-D4: JSON.stringify를 useMemo로 래핑하여 매 렌더링 연산 방지
  const employeeIncentiveInputsKey = useMemo(() =>
    JSON.stringify(employees.map(emp => ({
      id: emp.id, name: emp.name, team: emp.team,
      position: emp.position, salary: emp.salary,
      overtime: emp.overtime, individual: emp.individual
    }))),
    [employees]
  );

  const teamActualSalesKey = useMemo(() =>
    JSON.stringify(teamActualSales),
    [teamActualSales]
  );

  // 월별 우선 해석이므로 월별 teamIncentiveRatios 변경도 인센티브 재계산 트리거에 포함
  const teamIncentiveRatiosKey = useMemo(() =>
    JSON.stringify({
      global: budgetData.teamIncentiveRatios,
      monthly: budgetData.monthlyData?.[selectedMonth]?.teamIncentiveRatios
    }),
    [budgetData.teamIncentiveRatios, budgetData.monthlyData, selectedMonth]
  );

  // 인센티브 계산 로직 (useEffect로 분리하여 employees, budgetData 변경 시 자동 업데이트)
  useEffect(() => {
    // 🔒 월 데이터 로드 완료 가드: 월 전환 직후 employees에 이전 달 로스터가 잔존한 상태에서 실행되면
    //    이전 달 로스터+새 달 비율 계산 결과(로스터·teamTargetSales)가 새 달 monthlyData에 기입되므로,
    //    useMonthlyData의 로드 완료 신호가 selectedMonth와 일치할 때까지 대기 (완료 시 loadedMonth 변경으로 재트리거)
    if (loadedMonth !== selectedMonth) {
      return;
    }

    // 저장 중, 로딩 중, 또는 이미 계산 중이면 실행하지 않음
    if (isSavingRef.current || isLoading || isCalculatingIncentiveRef.current) {
      return;
    }

    // 계산 시작 플래그 설정
    isCalculatingIncentiveRef.current = true;

    if (!employees.length || !monthlyTeamTargetSales || Object.keys(monthlyTeamTargetSales).length === 0) {
      // 🔧 계산 중단 시에도 플래그 해제
      isCalculatingIncentiveRef.current = false;
      return; // 데이터가 없으면 계산 중단
    }
  
    // 🔧 메모이제이션된 팀별 통계 사용
  
    const newEmployeesWithIncentives = employees.map((emp, index) => {
      
      // 🔧 정본 함수로 통일: 실장 별도처리 + 선공제(directorDeduction)까지
      // calculateRealTimeIndividualIncentive가 담당한다. 기존 인라인 재구현은 '실장'을
      // else(팀원)로 오분류해 팀원 1인분을 배분하고 선공제를 누락하여 저장되는
      // emp.incentive가 정본과 어긋났음. (salary<=0은 정본이 0을 반환)
      const newIncentive = calculateRealTimeIndividualIncentive(emp);
      
      // 🔧 퇴직금은 인센티브 변경 시 재계산하지 않음 (순환 참조 방지)
      // 퇴직금 = (급여 + 초과수당 + 개별수당) / 12 (인센티브 제외)
      
      return {
        ...emp,
        incentive: newIncentive
        // retirement는 업데이트하지 않음
      };
    });
    
    // 전체 인센티브 통계 계산
    const totalOldIncentive = employees.reduce((sum, emp) => sum + (emp.incentive || 0), 0);
    const totalNewIncentive = newEmployeesWithIncentives.reduce((sum, emp) => sum + (emp.incentive || 0), 0);
    const incentiveChange = totalNewIncentive - totalOldIncentive;
    
    // 🧹 죽은 진단 계산 제거됨 (overallAchievementRate·theoreticalTotalIncentive·팀별 기여도 forEach — 소비처 없음)

    // 인센티브 변경으로 인한 상태 업데이트
    setEmployees(newEmployeesWithIncentives);
    
    // 🔧 팀별 인센티브 계산 상태와 동기화 - 개인 인센티브 변경이 팀별 집계에 즉시 반영
    const updatedTeamIncentiveCalculations = { ...teamIncentiveCalculations };
    
    // 각 팀별로 실제 지급된 인센티브 총액 계산하여 동기화
    localTeams.forEach(teamName => {
      if (teamName) {
        const teamEmployees = newEmployeesWithIncentives.filter(emp => emp.team === teamName);
        const teamIncentiveTotal = teamEmployees.reduce((sum, emp) => sum + (emp.incentive || 0), 0);
        
        // 기존 teamIncentiveCalculations에 실제 지급 인센티브 정보 추가/업데이트
        if (updatedTeamIncentiveCalculations[teamName]) {
          updatedTeamIncentiveCalculations[teamName] = {
            ...updatedTeamIncentiveCalculations[teamName],
            actualPaidIncentive: teamIncentiveTotal // 실제 지급된 인센티브 총액
          };
        }
        
      }
    });
    
    // 팀별 인센티브 계산 상태 업데이트
    setTeamIncentiveCalculations(updatedTeamIncentiveCalculations);
    
    // 🔧 계산 완료 플래그 해제
    isCalculatingIncentiveRef.current = false;
    
    // 🔧 인센티브 재계산 후 목표매출 재계산 로직 추가
    
    // 인센티브 변경이 있는 경우에만 목표매출 재계산
    let recalcCancelled = false;
    let recalcTimerFired = false;
    let targetSalesRecalcTimer: ReturnType<typeof setTimeout> | undefined;
    // 🔧 직전 실행의 pending 기입이 cleanup으로 파기됐다면(무관 deps 변화) 동일 월 한정으로 재평가 —
    //    재실행에서 incentiveChange가 0이어도 취소된 기입을 대신할 경로가 없어 영구 소실되던 회귀 창 봉합.
    //    월이 다르면(월 전환 파기) 소비하지 않으므로 교차 월 오염 방지는 그대로 유지된다.
    const hasPendingRecalcForThisMonth = pendingTargetSalesRecalcMonthRef.current === selectedMonth;
    if (incentiveChange !== 0 || hasPendingRecalcForThisMonth) {
      // 🔒 소거는 '이 월의 마커 소비' 시점으로 한정 — incentiveChange로 진입한 경우 다른 월(A월)의
      //    마커를 소비 없이 파기하면 취소된 목표 기입 재평가가 유실된다. 다른 월 마커는 보존한다
      //    (cleanup :2954-2956 계약·타이머 월 가드 :2840과 정합, 교차 월 오염은 그대로 차단).
      if (hasPendingRecalcForThisMonth) {
        pendingTargetSalesRecalcMonthRef.current = null;
      }

      // 업데이트된 직원 데이터로 팀별 인건비 총액 재계산
      targetSalesRecalcTimer = setTimeout(() => {
        recalcTimerFired = true; // 발화 후 cleanup이 재예약하지 않도록 (기입 완료·월 가드 차단 모두 재예약 불필요)
        // 🔒 월 전환 시 지연 기입 파기: cleanup됐거나 로드 완료 월이 예약 시점의 selectedMonth와 다르면 실행 금지
        if (recalcCancelled || lastLoadedMonthRef.current !== selectedMonth) {
          return;
        }

        // 월별 데이터에서 기존 팀별 인건비율 가져오기
        const existingMonthlyTeamPersonnelPercentages = budgetData.monthlyData?.[selectedMonth]?.teamPersonnelPercentages || {};
        
        // 팀별로 새로운 목표매출 및 순수 목표매출 계산
        const newTeamTargetSales: {[key: string]: number} = {};
        const newTeamPureTargetSales: {[key: string]: number} = {};
        let hasChanges = false;
        
        localTeams.forEach(teamName => {
          if (teamName) {
            // 업데이트된 직원 데이터로 팀별 인건비 총액 계산
            const teamEmployees = newEmployeesWithIncentives.filter(emp => emp.team === teamName);
            // 🔒 실장 배분 정본: 실장을 1회만 계상 (인센티브 경로와 동일 정의)
            const teamSalaryTotal = calculateTeamSalaryTotal(newEmployeesWithIncentives, teamName, newEmployeesWithIncentives);
            
            // 기존 인건비율 유지
            const teamPersonnelPercentage = existingMonthlyTeamPersonnelPercentages[teamName] || 0;
            
            if (teamPersonnelPercentage > 0) {
              // 순수 목표매출 계산 (지난달 미달성 조정 전)
              const newTeamPureTargetSales_calc = Math.round(teamSalaryTotal / (teamPersonnelPercentage / 100));
              
              // 🔧 중앙집중식 최종 목표매출 계산 (중복 방지 및 일관성 보장)
              const finalTargetResult = calculateFinalTargetSales(
                teamName,
                newTeamPureTargetSales_calc,
                teamEmployees,
                'auto' // 인센티브 자동 계산 컨텍스트
              );

              const newTeamTargetSales_calc = finalTargetResult.finalTargetSales;
              const adjustmentApplied = finalTargetResult.adjustmentApplied;
              const previousUnderperformance = finalTargetResult.underperformanceAmount;
              
              const oldTeamTargetSales = monthlyTeamTargetSales[teamName] || 0;
              const oldTeamPureTargetSales = budgetData.monthlyData?.[selectedMonth]?.teamPureTargetSales?.[teamName] || 0;
              
              newTeamPureTargetSales[teamName] = newTeamPureTargetSales_calc;
              newTeamTargetSales[teamName] = newTeamTargetSales_calc;
              
              if (Math.abs(newTeamTargetSales_calc - oldTeamTargetSales) >= 1) {
                hasChanges = true;
              }
            } else {
              newTeamPureTargetSales[teamName] = budgetData.monthlyData?.[selectedMonth]?.teamPureTargetSales?.[teamName] || 0;
              newTeamTargetSales[teamName] = monthlyTeamTargetSales[teamName] || 0;
            }
          }
        });
        
        if (hasChanges) {
          
          // 새로운 전체 목표매출 계산
          const newTotalTargetSales = Object.values(newTeamTargetSales).reduce((sum, val) => sum + (Number(val) || 0), 0);
          
          // 새로운 전체 인건비 총액 계산 (업데이트된 직원 데이터 기준)
          const newTotalBaseSalaryAndAllowances = newEmployeesWithIncentives.reduce((sum, emp) => {
            return sum + emp.salary + (emp.retirement || 0) + (emp.meal || 0) + (emp.housing || 0) + 
              (emp.overtime || 0) + (emp.individual || 0) + (emp.other || 0);
          }, 0);
          
          // 🔧 인건비율 자동 조정 기능 비활성화 - 기존 인건비율 유지
          const newPersonnelPercentage = budgetData.monthlyData?.[selectedMonth]?.personnelPercentage || budgetData.personnelPercentage || 19;
          
          // budgetData 업데이트 (완전한 깊은 복사)
          setBudgetData(prev => {
            const updatedMonthlyData = JSON.parse(JSON.stringify(prev.monthlyData || {}));
            updatedMonthlyData[selectedMonth] = {
              ...JSON.parse(JSON.stringify(prev.monthlyData?.[selectedMonth] || {})),
              teamTargetSales: newTeamTargetSales,
              teamPureTargetSales: newTeamPureTargetSales,
              targetSales: newTotalTargetSales,
              personnelPercentage: newPersonnelPercentage,
              teamPersonnelPercentages: existingMonthlyTeamPersonnelPercentages,
              actualSales: prev.monthlyData?.[selectedMonth]?.actualSales || prev.actualSales,
              employees: newEmployeesWithIncentives
            };
            
            const updatedBudgetData = {
              ...prev,
              teamTargetSales: newTeamTargetSales,
              teamPureTargetSales: newTeamPureTargetSales,
              targetSales: newTotalTargetSales,
              personnelPercentage: newPersonnelPercentage,
              currentPersonnelBudget: newTotalBaseSalaryAndAllowances,
              monthlyData: updatedMonthlyData
            };
            
            // 자동 저장 제거 - 수동 저장으로 변경
            // 변경사항만 추적하고 실제 저장은 저장 버튼 클릭 시에만 수행
            setIsDataChanged(true);
            
            return updatedBudgetData;
          });
          
        } else {
        }
      }, 100); // 100ms 지연으로 setEmployees 완료 후 실행

    } else {
    }

    // 🔒 월 전환·의존성 변경 시 예약된 지연 기입 파기 (이전 달 데이터가 새 달 monthlyData에 기입되는 것 방지)
    return () => {
      recalcCancelled = true;
      if (targetSalesRecalcTimer !== undefined) {
        clearTimeout(targetSalesRecalcTimer);
        // 🔧 미발화 타이머를 파기한 경우 '이 월의 기입이 취소됨'을 예약 — 동일 월 재실행에서만 소비된다.
        //    월 전환 파기 시에는 마커의 월(클로저 selectedMonth)이 새 월과 달라 소비되지 않으므로
        //    교차 월 오염 봉합(무조건 파기 + 콜백 월 가드)은 전혀 후퇴하지 않는다.
        if (!recalcTimerFired) {
          pendingTargetSalesRecalcMonthRef.current = selectedMonth;
        }
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedMonth,
    loadedMonth, // 🔒 월 데이터 로드 완료 신호 (로드 완료 시 가드 해제 후 재계산 재트리거)
    // 🔧 FIX C10-D4: JSON.stringify → useMemo 래핑으로 매 렌더링 연산 방지
    employeeIncentiveInputsKey, // 인센티브 계산 입력 필드만 추적 (인센티브 필드 제외 → 순환 방지)
    teamActualSalesKey, // useMemo 래핑된 깊은 비교
    budgetData.incentiveRatios?.performance,
    budgetData.incentiveRatios?.team,
    budgetData.incentiveRatios?.member,
    underperformanceDeductionRate,
    teamIncentiveRatiosKey, // useMemo 래핑된 팀별 인센티브 비율
  ]);

  // 🔧 월 전환 시 stale 표시 방지: 다른 달 실적이 화면에 잔류하지 않도록 즉시 클리어
  //    (2026-06 화면에 7월 실적 3,058,090이 표시되던 stale-display 사고 방지)
  useEffect(() => {
    setTeamActualSales({});
  }, [selectedMonth]);

  // 가드에 막혀 스킵된 로드를 재시도하기 위한 tick (스킵 후 영원히 재시도되지 않던 버그 방지)
  const [actualSalesReloadTick, setActualSalesReloadTick] = useState(0);

  // 팀별 실제매출 로드 (ActualSalesService 사용으로 변경 - 더 정확한 데이터)
  useEffect(() => {
    let cancelled = false;
    const scheduleRetry = () => {
      setTimeout(() => { if (!cancelled) setActualSalesReloadTick(t => t + 1); }, 300);
    };
    const loadTeamActualSalesData = async () => {

      // 🔧 더 안전한 조건 검사
      if (!selectedMonth) {
        return;
      }

      // 🔧 가드에 막히면 재시도 예약 (월 전환 직후 계산/로딩 중이면 이전엔 영원히 스킵됐음)
      if (isLoading || isCalculatingIncentiveRef.current) {
        scheduleRetry();
        return;
      }

      if (!globalEmployees || globalEmployees.length === 0) {
        return;
      }

      // 이미 로딩 중이면 재시도 예약 (다른 달 로드가 끝난 뒤 이 달을 다시 로드)
      if (isLoadingActualSales) {
        scheduleRetry();
        return;
      }

      try {
        setIsLoadingActualSales(true);
        
        // 🆕 ActualSalesService를 사용한 정확한 실제매출 계산
        const { ActualSalesService } = await import('../../services/actualSalesService.ts');
        
        const actualSalesResult = await ActualSalesService.calculateMonthlyActualSales(selectedMonth, globalEmployees, budgetData);

        // 🔧 로드 중 월이 바뀌었으면 stale 결과를 새 달 화면에 쓰지 않음
        if (cancelled) return;

        // 팀별 실제매출 데이터 추출
        const teamActualSalesData = actualSalesResult.teamActualSales;
        const totalActual = actualSalesResult.totalActualSales;

        // 상태 업데이트
        setTeamActualSales(teamActualSalesData);
        
        // 상태 업데이트 확인을 위한 타이머
        setTimeout(() => {
        }, 100);
        
        // budgetData의 actualSales 업데이트
        setBudgetData(prev => {
          return {
          ...prev,
          actualSales: totalActual
          };
        });
        
        // 🔧 원본 데이터도 동시 업데이트하여 변경사항으로 감지되지 않도록 수정
        setOriginalBudgetData(prev => {
          if (prev) {
            return {
              ...prev,
              actualSales: totalActual
            };
          }
          return prev;
        });
        
      } catch (error) {
        console.error('[급여관리 → ActualSalesService] 실제매출 데이터 로드 실패:', error);
        // 에러 시 빈 객체로 초기화
        setTeamActualSales({});
        
        // 사용자에게 알림 (선택적)
        if (error instanceof Error) {
          console.warn(`[급여관리 → ActualSalesService] 실제매출 로드 오류: ${error.message}`);
        }
      } finally {
        setIsLoadingActualSales(false);
      }
    };

    // 초기 로드 및 의존성 변경 시 실행
    loadTeamActualSalesData();
    return () => { cancelled = true; };
  // 🔧 FIX C10-D5: JSON.stringify/join 제거 - 참조 기반 변경 감지 (+가드 스킵 재시도 tick)
  }, [selectedMonth, globalEmployees, isLoading, localTeams, budgetData?.monthlyData?.[selectedMonth]?.employees?.length, actualSalesReloadTick]);

  // 🔥 teamActualSales 상태 변화 추적 useEffect
  useEffect(() => {
  }, [teamActualSales, selectedMonth]);

  // 🔧 인센티브 산출 매출 계산 useEffect
  useEffect(() => {
    // budgetData에서 팀별 실제매출 데이터 가져오기
    const teamActualSalesFromBudget: Record<string, number> = {};
    
    // budgetData의 teamPerformanceData에서 실제매출 추출
    if (budgetData.monthlyData?.[selectedMonth]?.teamPerformanceData) {
      Object.entries(budgetData.monthlyData[selectedMonth].teamPerformanceData).forEach(([teamName, performanceData]) => {
        if (performanceData && typeof performanceData.actualSales === 'number') {
          teamActualSalesFromBudget[teamName] = performanceData.actualSales;
        }
      });
    }
    
    // teamActualSales 대신 budgetData에서 가져온 데이터 사용
    const actualSalesData = Object.keys(teamActualSalesFromBudget).length > 0 
      ? teamActualSalesFromBudget 
      : teamActualSales;

    if (localTeams.length === 0 || Object.keys(actualSalesData).length === 0) {
      return;
    }

    // 팀별 인건비 총액 계산 (실장 인건비 배분 포함)
    const teamSalaryTotals: Record<string, number> = {};
    localTeams.forEach(teamName => {
      // 🔒 실장 배분 정본: 실장 1회 계상 (기존 base+배분 변수분리형 이중계상 제거)
      teamSalaryTotals[teamName] = calculateTeamSalaryTotal(employees, teamName, employees);
    });

    // 인센티브 산출 매출 계산 실행
    const calculations = calculateIncentiveCalculationSales(
      localTeams,
      actualSalesData, // 수정: budgetData에서 가져온 데이터 사용
      budgetData.monthlyData?.[selectedMonth]?.teamTargetSales || {}, // 🔧 수정: 월별 데이터 사용
      teamSalaryTotals,
      underperformanceDeductionRate
    );

    setTeamIncentiveCalculations(calculations);

  // 🔧 가장 중요한 수정: budgetData.monthlyData 의존성 제거하여 무한 렌더링 방지
  // 🚀 수정: employees.length → employees로 변경하여 월급 변경 시 실시간 업데이트 되도록 함
  }, [localTeams, teamActualSales, selectedMonth, employees, underperformanceDeductionRate]);

  const updateTeamIncentiveRatio = async (teamName: string, type: 'performance' | 'team' | 'member', value: string | number) => {
    const fieldId = `teamIncentiveRatio_${teamName}_${type}`;
    const numValue = Number(value) || 0;
    // 🎯 월별 우선 해석값 기준으로 시드 (팀장/팀원 비율 유실 방지)
    const currentRatio = resolveTeamIncentiveRatio(budgetData, selectedMonth, teamName);
    const originalValue = currentRatio[type] || 0;

    markFieldAsChanged(fieldId, numValue, originalValue);

    // 변경된 비율 계산 (팀장/팀원 합 100 자동 보정 유지)
    let updatedRatios = { ...currentRatio, [type]: numValue };
    if (type === 'team') updatedRatios.member = Math.max(0, 100 - numValue);
    else if (type === 'member') updatedRatios.team = Math.max(0, 100 - numValue);

    // 🔒 월별만 기록 + 전역 동결: monthlyData[선택월].teamIncentiveRatios 에만 저장 (root 미갱신)
    const currentMonthData = getSafeMonthlyData(budgetData, selectedMonth);
    const updatedBudgetData = {
      ...budgetData,
      monthlyData: {
        ...budgetData.monthlyData,
        [selectedMonth]: {
          ...currentMonthData,
          teamIncentiveRatios: {
            ...(currentMonthData.teamIncentiveRatios || {}),
            [teamName]: updatedRatios
          },
          lastUpdated: new Date().toISOString()
        }
      }
    };

    // 로컬 상태 업데이트
    setBudgetData(updatedBudgetData);
    setIsDataChanged(true);

    // 명시저장 전환: 즉시 저장하지 않고 in-memory 반영만(위 setBudgetData + markFieldAsChanged +
    // setIsDataChanged). 실제 Firebase 저장은 '저장' 버튼(internalSaveAllData)이 수행한다.
    addChangeLog(`${teamName} 팀 인센티브 ${type === 'performance' ? '성과' : type === 'team' ? '팀장' : '팀원'} 비율: ${numValue}% (변경됨 · 미저장)`);
  };

// 월별 직원 비교 데이터를 렌더링하는 함수
const renderMonthlyComparison = () => {
  if (!budgetData.monthlyData) return null;
  
  const otherMonths = Object.keys(budgetData.monthlyData || {})
    .filter(month => month !== selectedMonth && budgetData.monthlyData?.[month].employees)
    .sort((a, b) => b.localeCompare(a)) // 최근 월부터 정렬
    .slice(0, 2); // 최근 2개월만
  
  if (otherMonths.length === 0) return null;
  
  return otherMonths.map(month => {
    const monthEmployees = budgetData.monthlyData?.[month]?.employees || [];

    // 급여 0원 직원 제외
    const paidEmployees = employees.filter(emp => emp.salary > 0);
    const paidMonthEmployees = monthEmployees.filter(emp => emp.salary > 0);

    // 직원 수 변동 계산
    const empCountDiff = paidEmployees.length - paidMonthEmployees.length;

    // 평균 급여 계산
    const currentAvgSalary = paidEmployees.length > 0
      ? paidEmployees.reduce((sum, emp) => sum + emp.salary, 0) / paidEmployees.length
      : 0;

    const compAvgSalary = paidMonthEmployees.length > 0
      ? paidMonthEmployees.reduce((sum, emp) => sum + emp.salary, 0) / paidMonthEmployees.length
      : 0;
    
    const avgSalaryDiff = currentAvgSalary - compAvgSalary;
    const avgSalaryDiffPercentage = compAvgSalary > 0 
      ? (avgSalaryDiff / compAvgSalary) * 100 
      : 0;
    
    return (
      <div key={month} className="w-full md:w-1/2 lg:w-1/3 px-2 mb-4">
        <div className="border rounded-lg p-3 bg-gray-50">
          <h3 className="text-md font-medium text-gray-700 mb-2">
            {month.replace('-', '년 ')}월 대비
          </h3>
          
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm">직원 수 변동:</span>
              <span className={`font-semibold ${empCountDiff > 0 ? 'text-green-600' : empCountDiff < 0 ? 'text-red-600' : ''}`}>
                {empCountDiff > 0 ? '+' : ''}{empCountDiff}명
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-sm">평균 급여 변동:</span>
              <span className={`font-semibold ${avgSalaryDiff > 0 ? 'text-green-600' : avgSalaryDiff < 0 ? 'text-red-600' : ''}`}>
                {avgSalaryDiff > 0 ? '+' : ''}{Math.round(avgSalaryDiff).toLocaleString()}원 
                ({avgSalaryDiffPercentage > 0 ? '+' : ''}{avgSalaryDiffPercentage.toFixed(1)}%)
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-sm">팀장 수 변동:</span>
              <span className={`font-semibold ${
                paidEmployees.filter(emp => emp.position === '팀장').length -
                paidMonthEmployees.filter(emp => emp.position === '팀장').length > 0
                  ? 'text-green-600'
                  : paidEmployees.filter(emp => emp.position === '팀장').length -
                    paidMonthEmployees.filter(emp => emp.position === '팀장').length < 0
                    ? 'text-red-600'
                    : ''
              }`}>
                {paidEmployees.filter(emp => emp.position === '팀장').length -
                 paidMonthEmployees.filter(emp => emp.position === '팀장').length > 0 ? '+' : ''}
                {paidEmployees.filter(emp => emp.position === '팀장').length -
                 paidMonthEmployees.filter(emp => emp.position === '팀장').length}명
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  });
}; 

  return {
    // State
    employees,
    setEmployees,
    previousMonthEmployees,
    importedFromMonth,
    teamActualSales,
    teamIncentiveCalculations,
    previousMonthData,
    setPreviousMonthData,
    originalTeams,
    originalTargetSales,
    setOriginalTargetSales,
    originalEmployees: undefined as any, // set via setOriginalEmployees
    originalBudgetData: undefined as any, // set via setOriginalBudgetData
    fieldChangeStatus,
    pendingTeamRemoval,
    fieldSaveStatus,
    savingFields,
    localTeams,
    setLocalTeams,
    checkedItems,
    setCheckedItems,
    allChecked,
    setAllChecked,
    // Refs
    isSavingRef,
    isCalculatingIncentiveRef,
    isAddingEmployeeRef,
    isManualRecalculatingRef,
    // Field tracking
    markFieldAsChanged,
    markFieldAsSaved,
    getFieldStyle,
    SaveStatusIcon,
    // Computed
    totalIncentive,
    monthlyTeamTargetSales,
    totalTargetSales,
    teamStatsData,
    isDataChanged,
    setIsDataChanged,
    lastSaveTime,
    changeLog,
    setChangeLog,
    teamSortType,
    setTeamSortType,
    // Functions
    ensureUnassignedTeamExists,
    getSortedTeams,
    addChangeLog,
    addTeam,
    removeTeam,
    updateEmployeeTeam,
    updateNonTaxable,
    updateEmployeeSalary,
    updateEmployeeFamilyCount,
    updateEmployeePosition,
    saveLimitedPayrollData,
    updateDirectorIncentiveRate,
    updateDirectorTeamAllocation,
    updatePersonnelPercentage,
    getOriginalValue,
    updateAllowance,
    updateOvertimeMinutes,
    updateEmployeeName,
    toggleAllChecked,
    toggleChecked,
    addEmployee,
    deleteEmployees,
    deleteEmployeesGlobally,
    user,
    validateDataBeforeSave,
    internalSaveAllData,
    saveSpecificMonthData,
    updateTeamIncentiveRatio,
    // 차감비율도 markFieldAsChanged로 dirty를 남겨야 '저장' 버튼이 활성화됨(setIsDataChanged만으로는
    // fieldChangeStatus가 비어 곧 리셋되어 저장 불가). updateTeamIncentiveRatio와 동일 안전망.
    updateUnderperformanceDeductionRate: (value: string | number) => {
      const orig = budgetData.monthlyData?.[selectedMonth]?.underperformanceDeductionRate
        ?? budgetData.underperformanceDeductionRate ?? 50;
      updateUnderperformanceDeductionRateFromCalc(value);
      markFieldAsChanged('underperformanceDeductionRate', Number(value) || 0, orig);
    },
    renderMonthlyComparison,
    // From usePayrollCalculations (exposed for render context)
    calculateDeductions,
    calculateNetSalaryWithAllowances,
    calculateRealTimeIndividualIncentive,
    getEffectivePersonnelPercentage,
    // From useMonthlyData (exposed for render context)
    handleImportPreviousMonth,
    handleAdjustmentToggle,
    lastLoadedMonthRef,
  };
}
